import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ClientEmailAutocompleteInput from '@/components/ClientEmailAutocompleteInput';
import EntityLogo from '@/assets/logos/EntityLogo';
import { useAuth } from '@/components/Auth/AuthContext';
import { useIsNarrowScreen } from '@/hooks/useMediaQuery';
import { StatusBadge } from '@/ui/components';
import { buildInvoicePaymentReference } from '@/utils/invoiceReference';
import { formatPhoneForDisplay, normalizePhoneForStorage } from '@/utils/phone';
import { canInvoiceEntityUseStripe, formatStripePaymentLabelForPdf, getVisibleStripePaymentUrl } from '@/utils/stripePaymentLabel';
import {
  getDefaultStripeSurchargeEnabled,
  isStripeSurchargeAllowed,
  resolveStripeSurchargeEnabledForUi,
  STRIPE_SURCHARGE_EXPIRY_DISPLAY_DATE,
} from '@/utils/stripeSurchargePolicy';
import { formatSuburbLabel } from '@/utils/suburbText';
import { hasRequiredRoleAccess } from '@/utils/swaRole';
import { formatUserFacingError } from '@/utils/userFacingErrors';
import { getInvoicePaymentState } from '@/pages/Invoices/invoiceStatus';
import InvoiceWorkflowPhoneSheet from './InvoiceWorkflowPhoneSheet';
import './workflowDataGridFocus.css';

type WorkflowMode = 'create' | 'amend';
type InvoiceSource = 'current' | 'civil';
type InvoiceType = 'standard' | 'inspection';
type WorkflowTab = 'sheet' | 'amend' | 'finalise' | 'categories';
type StripePaymentMode = 'none' | 'payment_link' | 'checkout_session';
type EditableInvoiceField = Exclude<keyof EditorForm, 'invoiceType' | 'stripeSurchargeEnabled' | 'items'>;
type FeedbackTone = 'danger' | 'success' | 'warning';
const INVOICE_WORKFLOW_SESSION_DRAFT_KEY = 'pmapp.invoiceWorkflow.sessionDraft.v1';
const INVOICE_WORKFLOW_SESSION_DRAFT_MAX_AGE_MS = 12 * 60 * 60 * 1000;

type Category = {
  CategoryId: number;
  CategoryCode?: string;
  CategoryName: string;
  DefaultDescription?: string;
  IsActive?: boolean;
};

type ProjectOption = {
  ProjectId: number;
  ProjectCode?: string;
  ProjectName?: string;
  SiteAddress?: string;
  Suburb?: string;
  PostCode?: string | number;
};

type InvoiceOption = {
  InvoiceId?: number;
  InvoiceNumber?: string;
  InvoiceType?: string;
  InspectionJobId?: number;
  Client?: string;
  Description?: string;
  Status?: string;
  CurrentRevisionNo?: number;
  CurrentRevisionStatus?: string;
  LatestIssuedRevisionNo?: number;
  LatestIssuedRevisionStatus?: string;
  HasPendingRevision?: boolean;
  SupersedesRevisionNo?: number;
};

type ClientOption = {
  ClientId?: number;
  Name?: string;
  Company?: string;
  Email?: string;
  Phone?: string;
  Address?: string;
};

type QuotationClientOption = {
  QuotationId?: number;
  QuotationNumber?: string;
  ClientName?: string;
  CompanyName?: string;
  Address?: string;
  Suburb?: string;
  PostCode?: string | number;
  ProjectLocation?: string;
  ClientAddress?: string;
  ClientMobile?: string;
  ClientEmail?: string;
};

type ItemRow = {
  categoryId: string;
  categoryName: string;
  description: string;
  quantity: string;
  unitPrice: string;
  taxRate: string;
};

type InvoiceWorkflowPrefillState = {
  mode?: WorkflowMode;
  prefill?: Partial<EditorForm> & {
    items?: Array<Partial<ItemRow>>;
    successMessage?: string;
  };
  invoiceId?: string | number;
  invoiceNumber?: string;
};

type EditorForm = {
  invoiceType: InvoiceType;
  inspectionJobId: string;
  inspectionFolderPath: string;
  inspectionFolderUrl: string;
  invoiceId: string;
  invoiceNumber: string;
  projectId: string;
  projectNumber: string;
  taskId: string;
  rfiTicketNumber: string;
  entity: string;
  clientName: string;
  clientCompany: string;
  clientAddress: string;
  clientMobile: string;
  clientEmail: string;
  projectAddress: string;
  invoiceDate: string;
  dueDate: string;
  notes: string;
  stripePaymentMode: StripePaymentMode;
  stripeSurchargeEnabled: boolean;
  stripePaymentUrl: string;
  stripePaymentReference: string;
  paymentStatus: string;
  items: ItemRow[];
};

type InvoiceDraftResponse = {
  source?: InvoiceSource;
  invoiceType?: string | null;
  inspectionJobId?: number | null;
  inspectionAddress?: string | null;
  inspectionJob?: {
    inspectionJobId?: number | null;
    inspectionNumber?: string | null;
    sharePointFolderPath?: string | null;
    sharePointFolderUrl?: string | null;
    invoiceFolderPath?: string | null;
    invoiceFolderUrl?: string | null;
    photosFolderPath?: string | null;
    photosFolderUrl?: string | null;
    reportFolderPath?: string | null;
    reportFolderUrl?: string | null;
    lastInvoiceFileUrl?: string | null;
  } | null;
  invoiceId?: number;
  invoiceNumber?: string;
  taskId?: number;
  rfiTicketNumber?: string;
  stripePaymentMode?: StripePaymentMode;
  stripeSurchargeEnabled?: boolean | null;
  stripePaymentUrl?: string;
  stripePaymentReference?: string;
  paymentStatus?: string;
  status?: string;
  revisionNo?: number;
  latestIssuedRevisionNo?: number | null;
  latestIssuedRevisionStatus?: string | null;
  hasPendingRevision?: boolean;
  supersedesRevisionNo?: number | null;
  revisions?: Array<{
    revisionNo?: number;
    status?: string;
    isLocked?: boolean;
    isSuperseded?: boolean;
    supersedesRevisionNo?: number | null;
    supersededByRevisionNo?: number | null;
  }>;
  projectId?: number;
  projectNumber?: string;
  entity?: string;
  clientName?: string;
  clientCompany?: string;
  clientAddress?: string;
  clientMobile?: string;
  clientEmail?: string;
  recipientEmail?: string | null;
  ccEmails?: string[] | null;
  projectAddress?: string;
  invoiceDate?: string;
  dueDate?: string;
  notes?: string;
  items?: Array<{
    categoryId?: number | null;
    categoryName?: string | null;
    description?: string;
    quantity?: number;
    unitPrice?: number;
    taxRate?: number;
  }>;
  storedDraftPdf?: StoredDraftPdf | null;
};

type StoredDraftPdf = {
  versionNo?: number;
  draftFileId?: string;
  fileName?: string | null;
  contentPath?: string | null;
  contentType?: string | null;
  uploadedBy?: string | null;
  createdDate?: string | null;
};

type SendLockEmailResult = {
  attempted?: boolean;
  sent?: boolean;
  manual?: boolean;
  mode?: 'graph' | 'manual' | 'none';
  sender?: string | null;
  recipient?: string | null;
  recipients?: string[] | null;
  ccRecipients?: string[] | null;
  subject?: string | null;
  error?: string | null;
};

type SendLockSharePointUpload = {
  plannedPath?: string | null;
  webUrl?: string | null;
  fileName?: string | null;
  driveId?: string | null;
  itemId?: string | null;
  message?: string | null;
};

type SendLockResult = {
  workflow?: string;
  source?: InvoiceSource;
  invoiceType?: string;
  inspectionJobId?: string | number;
  inspectionJob?: InvoiceDraftResponse['inspectionJob'];
  invoiceId?: string | number;
  invoiceNumber?: string;
  versionNo?: number;
  revisionNo?: number;
  supersededRevisionNo?: number | null;
  projectId?: string | number;
  status?: string;
  taskId?: string | number;
  rfiTicketNumber?: string | null;
  stripePaymentUrl?: string | null;
  stripePaymentReference?: string | null;
  stripePaymentMode?: StripePaymentMode | null;
  stripeSurchargeEnabled?: boolean | null;
  sharePointUpload?: SendLockSharePointUpload | null;
  pdfSource?: string | null;
  draftFileId?: string | null;
  email?: SendLockEmailResult | null;
  persistenceWarning?: string;
  data?: {
    source?: InvoiceSource;
    invoiceType?: string;
    inspectionJobId?: string | number;
    inspectionJob?: InvoiceDraftResponse['inspectionJob'];
    invoiceId?: string | number;
    invoiceNumber?: string;
    versionNo?: number;
    revisionNo?: number;
    supersededRevisionNo?: number | null;
    projectId?: string | number;
    status?: string;
    taskId?: string | number;
    rfiTicketNumber?: string | null;
    stripePaymentUrl?: string | null;
    stripePaymentReference?: string | null;
    stripePaymentMode?: StripePaymentMode | null;
    stripeSurchargeEnabled?: boolean | null;
    sharePointUpload?: SendLockSharePointUpload | null;
    pdfSource?: string | null;
    draftFileId?: string | null;
    email?: SendLockEmailResult | null;
    persistenceWarning?: string;
  };
};

type InvoiceEmailPreviewResult = {
  invoiceNumber?: string;
  recipientEmail?: string | null;
  recipientEmails?: string[] | null;
  ccEmails?: string[] | null;
  senderEmail?: string | null;
  clientName?: string | null;
  projectNumber?: string | null;
  projectAddress?: string | null;
  projectSuburb?: string | null;
  dueDate?: string | null;
  amount?: number | null;
  stripePaymentUrl?: string | null;
  rfiTicketNumber?: string | null;
  entity?: string | null;
  subject?: string | null;
  html?: string | null;
};

type EmailComposerDraft = {
  subject: string;
  html: string;
};

type SendForm = {
  invoiceId: string;
  recipientEmail: string;
  additionalRecipientEmailsText: string;
  additionalCcEmailsText: string;
  ccEmails: string[];
  pdfFile: File | null;
};

type InspectionCalendarForm = {
  date: string;
  time: string;
  durationMinutes: string;
  attendeeEmail: string;
  description: string;
};

type InspectionCalendarResult = {
  event?: {
    id?: string | null;
    webLink?: string | null;
    iCalUId?: string | null;
  } | null;
  subject?: string | null;
  startDateTime?: string | null;
  endDateTime?: string | null;
  attendeeEmail?: string | null;
  calendarOwner?: string | null;
};

type InvoiceWorkflowSessionDraft = {
  savedAt: number;
  mode?: WorkflowMode;
  activeTab?: WorkflowTab;
  editorForm?: EditorForm;
  sendForm?: Omit<SendForm, 'pdfFile'>;
  quickSendOpen?: boolean;
  finaliseLookup?: string;
  invoiceLookup?: string;
  projectQuery?: string;
};

type PostSavePrompt = {
  invoiceId: string;
  invoiceNumber: string;
};

type LoadDraftOptions = {
  invoiceId?: string;
  invoiceNumber?: string;
  targetTab?: WorkflowTab;
  suppressSuccess?: boolean;
};

type PersistInvoiceForOutputResult = {
  form: EditorForm;
  locked: boolean;
};

const MIN_VISIBLE_ROWS = 7;

type EditableEmailHtmlProps = {
  value: string;
  onChange: (nextValue: string) => void;
  minHeight?: number;
};

const EditableEmailHtml: React.FC<EditableEmailHtmlProps> = ({ value, onChange, minHeight = 180 }) => {
  const editorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!editorRef.current) {
      return;
    }
    if (editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  return (
    <div
      ref={editorRef}
      contentEditable
      suppressContentEditableWarning
      onInput={(event) => onChange(event.currentTarget.innerHTML)}
      style={{
        minHeight,
        outline: 'none',
        lineHeight: 1.6,
        color: '#1f2937',
      }}
    />
  );
};

function toLocalYmd(date: Date) {
  const local = new Date(date.getTime() - (date.getTimezoneOffset() * 60_000));
  return local.toISOString().slice(0, 10);
}

function getTodayYmd() {
  return toLocalYmd(new Date());
}

function parseYmd(value?: string | null) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return null;
  const parsed = new Date(`${trimmed}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function addDaysYmd(baseDate: string, days: number) {
  const parsed = parseYmd(baseDate) || new Date();
  parsed.setDate(parsed.getDate() + days);
  return toLocalYmd(parsed);
}

function getDefaultInvoiceDates(baseDate = getTodayYmd()) {
  return {
    invoiceDate: baseDate,
    dueDate: addDaysYmd(baseDate, 7),
  };
}

function refreshInvoiceDates(form: EditorForm): EditorForm {
  const { invoiceDate, dueDate } = getDefaultInvoiceDates();
  return {
    ...form,
    invoiceDate,
    dueDate,
  };
}

const inputBase: React.CSSProperties = {
  width: '100%',
  border: '1px solid rgba(144, 163, 195, 0.34)',
  borderRadius: 10,
  padding: '10px 12px',
  fontSize: 14,
  boxSizing: 'border-box',
  background: 'rgba(255,255,255,0.98)',
  color: '#10243f',
};

const stripePaymentPreviewLinkStyle: React.CSSProperties = {
  color: '#34579e',
  fontWeight: 700,
  textDecoration: 'underline',
};

const sheetInput: React.CSSProperties = {
  width: '100%',
  border: '1px solid transparent',
  background: 'transparent',
  padding: 0,
  fontSize: 15,
  fontWeight: 600,
  color: '#111827',
  outline: 'none',
};

function ActionFeedbackLine({
  tone,
  message,
  children,
}: {
  tone?: FeedbackTone | null;
  message?: string | null;
  children?: React.ReactNode;
}) {
  if (!message && !children) return null;
  return (
    <div
      className={`wdgf-action-feedback${tone ? ` wdgf-action-feedback--${tone}` : ''}`}
      role={tone === 'danger' ? 'alert' : 'status'}
      aria-live={tone === 'danger' ? 'assertive' : 'polite'}
    >
      {message ? <span>{message}</span> : null}
      {children ? <span className="wdgf-action-feedback__actions">{children}</span> : null}
    </div>
  );
}

const emptyRow = (): ItemRow => ({
  categoryId: '',
  categoryName: '',
  description: '',
  quantity: '1',
  unitPrice: '0',
  taxRate: '0.1',
});

const emptyForm = (): EditorForm => ({
  invoiceType: 'standard',
  inspectionJobId: '',
  inspectionFolderPath: '',
  inspectionFolderUrl: '',
  invoiceId: '',
  invoiceNumber: '',
  projectId: '',
  projectNumber: '',
  taskId: '',
  rfiTicketNumber: '',
  entity: 'Linfinity Engineering Pty Ltd',
  clientName: '',
  clientCompany: '',
  clientAddress: '',
  clientMobile: '',
  clientEmail: '',
  projectAddress: '',
  ...getDefaultInvoiceDates(),
  notes: '',
  stripePaymentMode: 'none',
  stripeSurchargeEnabled: false,
  stripePaymentUrl: '',
  stripePaymentReference: '',
  paymentStatus: 'Unpaid',
  items: [emptyRow()],
});

function normalizeNumericInput(value: string, fallback = '0') {
  const trimmed = value.trim();
  if (!trimmed) return fallback;
  const n = Number(trimmed);
  return Number.isFinite(n) ? trimmed : fallback;
}

function normalizeLookup(value?: string | null) {
  return String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
}

function normalizeEmailInput(value?: string | null) {
  return String(value || '').trim().toLowerCase();
}

function dateInputFromDate(value: Date) {
  const pad = (input: number) => String(input).padStart(2, '0');
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
}

function createInspectionCalendarForm(attendeeEmail = '', description = ''): InspectionCalendarForm {
  const nextDay = new Date();
  nextDay.setDate(nextDay.getDate() + 1);
  return {
    date: dateInputFromDate(nextDay),
    time: '11:00',
    durationMinutes: '60',
    attendeeEmail,
    description,
  };
}

function combineLocalDateTime(date: string, time: string) {
  const normalizedDate = String(date || '').trim();
  const normalizedTime = String(time || '').trim();
  if (!normalizedDate || !normalizedTime) return '';
  return `${normalizedDate}T${normalizedTime.length === 5 ? `${normalizedTime}:00` : normalizedTime}`;
}

function buildInspectionCalendarSubject(address?: string | null, notes?: string | null) {
  const normalizedAddress = String(address || '').trim();
  const base = normalizedAddress ? `Inspection: ${normalizedAddress}` : 'Inspection';
  const normalizedNotes = String(notes || '').trim();
  if (!normalizedNotes) return base.length > 255 ? `${base.slice(0, 252)}...` : base;

  const fullSubject = `${base}, ${normalizedNotes}`;
  if (fullSubject.length <= 255) return fullSubject;
  return base.length > 255 ? `${base.slice(0, 252)}...` : base;
}

function inferStripePaymentMode(stripePaymentUrl?: string | null, stripePaymentReference?: string | null): StripePaymentMode {
  const reference = String(stripePaymentReference || '').trim().toLowerCase();
  if (reference.startsWith('plink_')) return 'payment_link';
  if (reference.startsWith('cs_')) return 'checkout_session';

  const paymentUrl = String(stripePaymentUrl || '').trim().toLowerCase();
  if (paymentUrl.includes('buy.stripe.com') || paymentUrl.includes('pay.stripe.com')) return 'payment_link';
  if (paymentUrl.includes('checkout.stripe.com')) return 'checkout_session';
  return 'none';
}

function normalizeStripePaymentMode(value?: string | null, stripePaymentUrl?: string | null, stripePaymentReference?: string | null): StripePaymentMode {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'payment_link') return 'payment_link';
  if (normalized === 'checkout_session') return 'checkout_session';
  if (normalized === 'none' || normalized === 'off' || normalized === 'disabled' || normalized === 'bank_transfer_only') return 'none';
  return inferStripePaymentMode(stripePaymentUrl, stripePaymentReference);
}

function normalizeStripeSurchargeEnabled(
  value: unknown,
  stripePaymentMode?: string | null,
  stripePaymentUrl?: string | null,
  stripePaymentReference?: string | null,
): boolean {
  return resolveStripeSurchargeEnabledForUi(
    value,
    normalizeStripePaymentMode(stripePaymentMode, stripePaymentUrl, stripePaymentReference),
  );
}

function parseEmailListInput(value?: string | string[] | null) {
  const rawValues = Array.isArray(value)
    ? value
    : String(value || '').split(/[,\n;]+/);
  const seen = new Set<string>();
  const result: string[] = [];
  for (const rawValue of rawValues) {
    const normalized = normalizeEmailInput(rawValue);
    if (!normalized || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(normalized) || seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    result.push(normalized);
  }
  return result;
}

function formatEmailListInput(values?: string[] | null) {
  return Array.isArray(values) ? values.join(', ') : '';
}

function buildEffectiveRecipientEmails(recipientEmail?: string | null, additionalRecipientEmailsText?: string | null) {
  return parseEmailListInput([
    ...parseEmailListInput(recipientEmail),
    ...parseEmailListInput(additionalRecipientEmailsText),
  ]);
}

function buildEffectiveCcEmails(input: {
  recipientEmail?: string | null;
  additionalRecipientEmailsText?: string | null;
  additionalCcEmailsText?: string | null;
  clientEmail?: string | null;
}) {
  const recipientEmails = buildEffectiveRecipientEmails(input.recipientEmail, input.additionalRecipientEmailsText);
  const seen = new Set(recipientEmails);
  const result: string[] = [];
  const clientEmails = parseEmailListInput(input.clientEmail);
  for (const email of clientEmails) {
    if (!seen.has(email)) {
      seen.add(email);
      result.push(email);
    }
  }
  for (const email of parseEmailListInput(input.additionalCcEmailsText)) {
    if (!seen.has(email)) {
      seen.add(email);
      result.push(email);
    }
  }
  return result;
}

function deriveManualCcEmailsText(ccEmails?: string[] | null, clientEmail?: string | null) {
  const normalizedClientEmails = new Set(parseEmailListInput(clientEmail));
  return formatEmailListInput(
    parseEmailListInput(ccEmails || []).filter((email) => !normalizedClientEmails.has(email)),
  );
}

function createSendForm(overrides?: Partial<SendForm>): SendForm {
  return {
    invoiceId: '',
    recipientEmail: '',
    additionalRecipientEmailsText: '',
    additionalCcEmailsText: '',
    ccEmails: [],
    pdfFile: null,
    ...overrides,
  };
}

function readInvoiceWorkflowSessionDraft(): InvoiceWorkflowSessionDraft | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(INVOICE_WORKFLOW_SESSION_DRAFT_KEY);
    if (!raw) return null;

    const draft = JSON.parse(raw) as InvoiceWorkflowSessionDraft;
    if (!draft || typeof draft !== 'object') return null;
    if (!draft.savedAt || Date.now() - draft.savedAt > INVOICE_WORKFLOW_SESSION_DRAFT_MAX_AGE_MS) {
      window.sessionStorage.removeItem(INVOICE_WORKFLOW_SESSION_DRAFT_KEY);
      return null;
    }

    return draft;
  } catch {
    return null;
  }
}

function writeInvoiceWorkflowSessionDraft(draft: InvoiceWorkflowSessionDraft) {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(INVOICE_WORKFLOW_SESSION_DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // Best-effort only. Losing the session backup must not block editing.
  }
}

function clearInvoiceWorkflowSessionDraft() {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.removeItem(INVOICE_WORKFLOW_SESSION_DRAFT_KEY);
  } catch {
    // Best-effort only.
  }
}

function isInvoiceLockedAfterSendError(err: any) {
  return Number(err?.status) === 409 && /invoice is locked after send/i.test(String(err?.message || ''));
}

function buildDraftContentLink(draft?: StoredDraftPdf | null) {
  if (draft?.contentPath) {
    return draft.contentPath;
  }
  if (draft?.draftFileId) {
    return `/api/v1/draft-files/content?id=${encodeURIComponent(draft.draftFileId)}`;
  }
  return null;
}

function formatCompactId(value?: string | null, visibleChars = 12) {
  const text = String(value || '').trim();
  if (!text) return 'Not linked yet';
  if (text.length <= visibleChars * 2) return text;
  return `${text.slice(0, visibleChars)}...${text.slice(-visibleChars)}`;
}

function mergeInvoiceClientDetails(
  form: EditorForm,
  nextValues: Partial<Pick<EditorForm, 'clientName' | 'clientCompany' | 'clientAddress' | 'clientMobile' | 'clientEmail'>>,
  replaceExisting = false,
): EditorForm {
  const pick = (current: string, incoming?: string) => {
    const normalizedIncoming = String(incoming || '').trim();
    if (!normalizedIncoming) {
      return current;
    }
    return replaceExisting || !String(current || '').trim() ? normalizedIncoming : current;
  };

  return {
    ...form,
    clientName: pick(form.clientName, nextValues.clientName),
    clientCompany: pick(form.clientCompany, nextValues.clientCompany),
    clientAddress: pick(form.clientAddress, nextValues.clientAddress),
    clientMobile: pick(
      form.clientMobile,
      nextValues.clientMobile ? (formatPhoneForDisplay(nextValues.clientMobile) || nextValues.clientMobile) : nextValues.clientMobile,
    ),
    clientEmail: pick(form.clientEmail, nextValues.clientEmail),
  };
}

function formatProjectAddressParts(address?: string | null, suburb?: string | null, postCode?: string | number | null) {
  const normalizedAddress = String(address || '').trim();
  const normalizedSuburb = formatSuburbLabel(suburb);
  const normalizedPostCode = String(postCode || '').trim();
  return [normalizedAddress, normalizedSuburb, normalizedPostCode].filter(Boolean).join(', ');
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error || new Error('Failed to read blob'));
    reader.readAsDataURL(blob);
  });
}

function itemHasContent(item: ItemRow) {
  return Boolean(
    item.description.trim()
    || item.categoryId.trim()
    || Number(item.quantity || 0) !== 0
    || Number(item.unitPrice || 0) !== 0
  );
}

function buildPayloadItems(items: ItemRow[]) {
  return items
    .filter(itemHasContent)
    .map((item) => ({
      categoryId: item.categoryId || undefined,
      categoryName: item.categoryName || undefined,
      description: item.description.trim(),
      quantity: normalizeNumericInput(item.quantity, '1'),
      unitPrice: normalizeNumericInput(item.unitPrice, '0'),
      taxRate: normalizeNumericInput(item.taxRate, '0.1'),
    }));
}

function formatMoney(amount: number) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  }).format(Number.isFinite(amount) ? amount : 0);
}

function formatDraftTimestamp(value?: string | null) {
  if (!value) return 'Not stored yet';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-AU');
}

function isInvoicePendingToSend(status?: string | null) {
  const normalized = String(status || '').trim().toLowerCase();
  if (!normalized) return true;
  return !['sent', 'filed', 'paid', 'cancelled', 'overdue'].includes(normalized);
}

function getSenderProfile(entity: string) {
  if (entity.trim().toLowerCase() === 'equilibrium engineering pty ltd') {
    return {
      displayName: 'Equilibrium Engineering Pty Ltd (ABN 24 624 618 435)',
      address: '1/21 Barry Street, Bayswater VIC 3153',
      mobile: '0421 900 119',
      email: 'wdu@eqeng.com.au',
      payId: '',
      bsb: '063-109',
      accountNumber: '1279-4847',
      accountName: 'Equilibrium Engineering Pty Ltd (ACN: 624 618 435)',
    };
  }

  return {
    displayName: 'Linfinity Engineering Pty Ltd (ABN 64 427 178 431)',
    address: 'Suite 261, Tenancy 111, Camberwell Place, 793 Burke Road, Camberwell, VIC 3124',
    mobile: '0468 886 926',
    email: 'yl@linfinityeng.com',
    payId: 'YL@linfinityeng.com',
    bsb: '013-479',
    accountNumber: '4177-69018',
    accountName: 'Linfinity Engineering Pty Ltd',
  };
}

function calculateTotals(items: ItemRow[]) {
  const usedItems = buildPayloadItems(items);
  const subTotal = usedItems.reduce((sum, item) => sum + (Number(item.quantity) * Number(item.unitPrice)), 0);
  const gst = usedItems.reduce((sum, item) => sum + (Number(item.quantity) * Number(item.unitPrice) * Number(item.taxRate)), 0);
  return {
    subTotal,
    gst,
    grandTotal: subTotal + gst,
    usedItems,
  };
}

function buildInvoicePdfPayload(form: EditorForm) {
  const totals = calculateTotals(form.items);
  const sender = getSenderProfile(form.entity);
  const visibleStripePaymentUrl = getVisibleStripePaymentUrl(form.entity, form.stripePaymentUrl);
  return {
    invoiceNumber: form.invoiceNumber,
    issueDate: form.invoiceDate,
    dueDate: form.dueDate,
    notes: form.notes,
    rfiTicketNumber: form.rfiTicketNumber,
    stripePaymentUrl: visibleStripePaymentUrl,
    stripeSurchargeEnabled: visibleStripePaymentUrl && isStripeSurchargeAllowed() ? form.stripeSurchargeEnabled : false,
    entity: form.entity,
    senderDisplayName: sender.displayName,
    senderAddress: sender.address,
    senderMobile: sender.mobile,
    senderEmail: sender.email,
    senderPayId: sender.payId,
    senderBsb: sender.bsb,
    senderAccountNumber: sender.accountNumber,
    senderAccountName: sender.accountName,
    clientName: form.clientName,
    companyName: form.clientCompany,
    clientAddress: form.clientAddress,
    clientMobile: normalizePhoneForStorage(form.clientMobile) || form.clientMobile,
    clientEmail: form.clientEmail,
    invoiceDetails: buildPayloadItems(form.items)
      .map((item) => `${item.description} [$${Number(item.quantity) * Number(item.unitPrice)}];`)
      .join(''),
    projectNumber: form.projectNumber,
    projectLocation: form.projectAddress,
    totalDue: totals.grandTotal,
    subtotal: totals.subTotal,
    gstAmount: totals.gst,
    paidAmount: 0,
    items: totals.usedItems.map((item) => ({
      categoryName: item.categoryName,
      description: item.description,
      rate: Number(item.unitPrice),
      quantity: Number(item.quantity),
      total: Number(item.quantity) * Number(item.unitPrice),
      exclGst: Number(item.taxRate) > 0,
    })),
  };
}

function openPdfBlob(blob: Blob, previewWindow?: Window | null) {
  const objectUrl = URL.createObjectURL(blob);
  const targetWindow = previewWindow && !previewWindow.closed ? previewWindow : window.open('', '_blank');

  if (targetWindow && !targetWindow.closed) {
    try {
      targetWindow.document.title = 'Invoice PDF Preview';
      targetWindow.document.body.innerHTML = '';
      targetWindow.document.body.style.margin = '0';
      const frame = targetWindow.document.createElement('iframe');
      frame.src = objectUrl;
      frame.title = 'Invoice PDF Preview';
      frame.style.width = '100vw';
      frame.style.height = '100vh';
      frame.style.border = '0';
      targetWindow.document.body.appendChild(frame);
    } catch {
      targetWindow.location.href = objectUrl;
    }
  } else {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.target = '_blank';
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 300_000);
}

function openPreviewWindow(message = 'Rendering PDF preview...') {
  const previewWindow = typeof window !== 'undefined' ? window.open('', '_blank') : null;
  if (previewWindow && !previewWindow.closed) {
    try {
      previewWindow.document.title = 'Preparing Invoice Preview';
      previewWindow.document.body.innerHTML = `
        <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;background:#f8fafc;color:#334155;font:600 16px Segoe UI, Arial, sans-serif;">
          ${message}
        </div>
      `;
      previewWindow.document.body.style.margin = '0';
    } catch {
      // Ignore document access issues and let the final open call fall back.
    }
  }
  return previewWindow;
}

function mapDraftToForm(draft: InvoiceDraftResponse): EditorForm {
  const invoiceDate = draft.invoiceDate || getTodayYmd();
  const items = Array.isArray(draft.items) && draft.items.length > 0
    ? draft.items.map((item) => ({
      categoryId: item.categoryId ? String(item.categoryId) : '',
      categoryName: item.categoryName || '',
      description: item.description || '',
      quantity: item.quantity != null ? String(item.quantity) : '1',
      unitPrice: item.unitPrice != null ? String(item.unitPrice) : '0',
      taxRate: item.taxRate != null ? String(item.taxRate) : '0.1',
    }))
    : [emptyRow()];

  return {
    invoiceType: String(draft.invoiceType || '').trim().toLowerCase() === 'inspection' ? 'inspection' : 'standard',
    inspectionJobId: draft.inspectionJobId || draft.inspectionJob?.inspectionJobId ? String(draft.inspectionJobId || draft.inspectionJob?.inspectionJobId || '') : '',
    inspectionFolderPath: draft.inspectionJob?.sharePointFolderPath || '',
    inspectionFolderUrl: draft.inspectionJob?.sharePointFolderUrl || '',
    invoiceId: draft.invoiceId ? String(draft.invoiceId) : '',
    invoiceNumber: draft.invoiceNumber || '',
    projectId: draft.projectId ? String(draft.projectId) : '',
    projectNumber: draft.projectNumber || '',
    taskId: draft.taskId ? String(draft.taskId) : '',
    rfiTicketNumber: draft.rfiTicketNumber || '',
    entity: draft.entity || 'Linfinity Engineering Pty Ltd',
    clientName: draft.clientName || '',
    clientCompany: draft.clientCompany || '',
    clientAddress: draft.clientAddress || '',
    clientMobile: formatPhoneForDisplay(draft.clientMobile),
    clientEmail: draft.clientEmail || '',
    projectAddress: draft.inspectionAddress || draft.projectAddress || '',
    invoiceDate,
    dueDate: draft.dueDate || addDaysYmd(invoiceDate, 7),
    notes: draft.notes || '',
    stripePaymentMode: normalizeStripePaymentMode(draft.stripePaymentMode, draft.stripePaymentUrl, draft.stripePaymentReference),
    stripeSurchargeEnabled: normalizeStripeSurchargeEnabled(
      draft.stripeSurchargeEnabled,
      draft.stripePaymentMode,
      draft.stripePaymentUrl,
      draft.stripePaymentReference,
    ),
    stripePaymentUrl: draft.stripePaymentUrl || '',
    stripePaymentReference: draft.stripePaymentReference || '',
    paymentStatus: draft.paymentStatus || 'Unpaid',
    items,
  };
}

function mapPrefillToForm(prefill?: InvoiceWorkflowPrefillState['prefill']): EditorForm {
  const base = emptyForm();
  const prefillItems = Array.isArray(prefill?.items) && prefill.items.length > 0
    ? prefill.items.map((item) => ({
      categoryId: String(item?.categoryId || '').trim(),
      categoryName: String(item?.categoryName || '').trim(),
      description: String(item?.description || '').trim(),
      quantity: String(item?.quantity || '1').trim() || '1',
      unitPrice: String(item?.unitPrice || '0').trim() || '0',
      taxRate: String(item?.taxRate || '0.1').trim() || '0.1',
    }))
    : base.items;

  return {
    ...base,
    ...prefill,
    invoiceType: String(prefill?.invoiceType || base.invoiceType).trim().toLowerCase() === 'inspection' ? 'inspection' : 'standard',
    inspectionJobId: String(prefill?.inspectionJobId || '').trim(),
    inspectionFolderPath: String(prefill?.inspectionFolderPath || '').trim(),
    inspectionFolderUrl: String(prefill?.inspectionFolderUrl || '').trim(),
    invoiceId: String(prefill?.invoiceId || '').trim(),
    invoiceNumber: String(prefill?.invoiceNumber || '').trim(),
    projectId: String(prefill?.projectId || '').trim(),
    projectNumber: String(prefill?.projectNumber || '').trim(),
    taskId: String(prefill?.taskId || '').trim(),
    rfiTicketNumber: String(prefill?.rfiTicketNumber || '').trim(),
    entity: String(prefill?.entity || base.entity).trim() || base.entity,
    clientName: String(prefill?.clientName || '').trim(),
    clientCompany: String(prefill?.clientCompany || '').trim(),
    clientAddress: String(prefill?.clientAddress || '').trim(),
    clientMobile: formatPhoneForDisplay(prefill?.clientMobile),
    clientEmail: String(prefill?.clientEmail || '').trim(),
    projectAddress: String(prefill?.projectAddress || '').trim(),
    invoiceDate: String(prefill?.invoiceDate || base.invoiceDate).trim() || base.invoiceDate,
    dueDate: String(prefill?.dueDate || base.dueDate).trim() || base.dueDate,
    notes: String(prefill?.notes || '').trim(),
    stripePaymentMode: normalizeStripePaymentMode(
      prefill?.stripePaymentMode as string | null | undefined,
      prefill?.stripePaymentUrl as string | null | undefined,
      prefill?.stripePaymentReference as string | null | undefined,
    ),
    stripeSurchargeEnabled: normalizeStripeSurchargeEnabled(
      prefill?.stripeSurchargeEnabled,
      prefill?.stripePaymentMode as string | null | undefined,
      prefill?.stripePaymentUrl as string | null | undefined,
      prefill?.stripePaymentReference as string | null | undefined,
    ),
    stripePaymentUrl: String(prefill?.stripePaymentUrl || '').trim(),
    stripePaymentReference: String(prefill?.stripePaymentReference || '').trim(),
    paymentStatus: String(prefill?.paymentStatus || base.paymentStatus).trim() || base.paymentStatus,
    items: prefillItems,
  };
}

function hasInvoiceIdentity(form: Pick<EditorForm, 'projectNumber' | 'invoiceNumber' | 'invoiceType'>) {
  if (form.invoiceType === 'inspection') {
    return true;
  }
  return Boolean(String(form.projectNumber || '').trim() || String(form.invoiceNumber || '').trim());
}

const InvoiceWorkflowPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const signedInEmail = normalizeEmailInput(user?.userDetails || '');
  const canManageStripePaymentLinks = signedInEmail === 'yl@linfinityeng.com';
  const isPhone = useIsNarrowScreen(900);
  const quickSendPanelRef = useRef<HTMLDivElement | null>(null);
  const nextInvoiceNumberRequestRef = useRef(0);
  const quotationHydrationRequestRef = useRef(0);
  const [mode, setMode] = useState<WorkflowMode>('create');
  const [activeTab, setActiveTab] = useState<WorkflowTab>('sheet');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([]);
  const [quotationClientOptions, setQuotationClientOptions] = useState<QuotationClientOption[]>([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [searchedClientOptions, setSearchedClientOptions] = useState<ClientOption[]>([]);
  const [clientSearchBusy, setClientSearchBusy] = useState(false);
  const [editorForm, setEditorForm] = useState<EditorForm>(() => emptyForm());
  const [projectQuery, setProjectQuery] = useState('');
  const [projectOptions, setProjectOptions] = useState<ProjectOption[]>([]);
  const [invoiceLookup, setInvoiceLookup] = useState('');
  const [invoiceOptions, setInvoiceOptions] = useState<InvoiceOption[]>([]);
  const [finaliseLookup, setFinaliseLookup] = useState('');
  const [invoiceSource, setInvoiceSource] = useState<InvoiceSource | null>(null);
  const [loadedInvoiceStatus, setLoadedInvoiceStatus] = useState('Draft');
  const [loadedRevisionNo, setLoadedRevisionNo] = useState<number>(1);
  const [loadedLatestIssuedRevisionNo, setLoadedLatestIssuedRevisionNo] = useState<number | null>(null);
  const [loadedLatestIssuedRevisionStatus, setLoadedLatestIssuedRevisionStatus] = useState<string | null>(null);
  const [loadedHasPendingRevision, setLoadedHasPendingRevision] = useState(false);
  const [loadedSupersedesRevisionNo, setLoadedSupersedesRevisionNo] = useState<number | null>(null);
  const [sendForm, setSendForm] = useState<SendForm>(() => createSendForm());
  const [quickSendOpen, setQuickSendOpen] = useState(false);
  const [storedDraftPdf, setStoredDraftPdf] = useState<StoredDraftPdf | null>(null);
  const [sendLockResult, setSendLockResult] = useState<SendLockResult | null>(null);
  const [emailPreview, setEmailPreview] = useState<InvoiceEmailPreviewResult | null>(null);
  const [emailComposerDraft, setEmailComposerDraft] = useState<EmailComposerDraft | null>(null);
  const [categoryForm, setCategoryForm] = useState({ categoryId: '', categoryCode: '', categoryName: '', defaultDescription: '' });
  const [postSavePrompt, setPostSavePrompt] = useState<PostSavePrompt | null>(null);
  const [workflowDraftRestored, setWorkflowDraftRestored] = useState(false);
  const [inspectionCalendarOpen, setInspectionCalendarOpen] = useState(false);
  const [inspectionCalendarForm, setInspectionCalendarForm] = useState<InspectionCalendarForm>(() => createInspectionCalendarForm());
  const [inspectionCalendarError, setInspectionCalendarError] = useState<string | null>(null);
  const [inspectionCalendarResult, setInspectionCalendarResult] = useState<InspectionCalendarResult | null>(null);

  const totals = useMemo(() => calculateTotals(editorForm.items), [editorForm.items]);
  const canManageInvoiceMutations = hasRequiredRoleAccess(user?.userRoles, user?.userDetails, ['finance']);
  const latestSendLockData = sendLockResult?.data || sendLockResult;
  const latestSharePointUpload = latestSendLockData?.sharePointUpload || null;
  const latestSharePointPath = latestSharePointUpload?.plannedPath || latestSharePointUpload?.webUrl || null;
  const latestSharePointLink = latestSharePointUpload?.webUrl || null;
  const latestInspectionJob = latestSendLockData?.inspectionJob || null;
  const isInspectionInvoice = editorForm.invoiceType === 'inspection';
  const inspectionFolderPath = latestInspectionJob?.sharePointFolderPath || editorForm.inspectionFolderPath;
  const inspectionFolderUrl = latestInspectionJob?.sharePointFolderUrl || editorForm.inspectionFolderUrl;
  const inspectionFolderReady = Boolean(String(inspectionFolderPath || '').trim());
  const inspectionCalendarCanOpen = isInspectionInvoice && String(latestSendLockData?.status || '').trim().toLowerCase() === 'sent';
  const inspectionCalendarSubject = useMemo(
    () => buildInspectionCalendarSubject(editorForm.projectAddress, editorForm.notes),
    [editorForm.projectAddress, editorForm.notes],
  );
  const busyMessage = useMemo(() => {
    if (!busy) return null;
    const labels: Record<string, string> = {
      create: 'Saving this invoice...',
      amend: 'Saving invoice changes...',
      'preview-draft': 'Rendering the draft PDF...',
      'load-draft': 'Loading invoice details...',
      'inspection-folder': 'Creating the SharePoint folder...',
      'inspection-calendar': 'Creating the inspection calendar invite...',
      'send-lock': 'Filing the invoice...',
      'send-lock-preview': 'Loading the email preview...',
      category: 'Saving category...',
      'mark-paid': 'Updating payment state...',
      'review-draft': 'Opening the stored draft...',
    };
    return labels[busy] || 'Working...';
  }, [busy]);
  const feedbackTone: FeedbackTone | null = error ? 'danger' : success ? 'success' : busyMessage ? 'warning' : null;
  const feedbackMessage = error || success || busyMessage;

  const sender = useMemo(() => getSenderProfile(editorForm.entity), [editorForm.entity]);
  const inspectionCalendarOwnerPreview = sender.email || signedInEmail || 'Invoice sender account';
  const stripeSurchargeAllowed = isStripeSurchargeAllowed();
  const canUseStripeForEntity = useMemo(() => canInvoiceEntityUseStripe(editorForm.entity), [editorForm.entity]);
  const visibleStripePaymentUrl = useMemo(
    () => getVisibleStripePaymentUrl(editorForm.entity, editorForm.stripePaymentUrl),
    [editorForm.entity, editorForm.stripePaymentUrl],
  );
  const latestStripeSurchargeEnabled = useMemo(
    () => normalizeStripeSurchargeEnabled(
      latestSendLockData?.stripeSurchargeEnabled ?? editorForm.stripeSurchargeEnabled,
      latestSendLockData?.stripePaymentMode ?? editorForm.stripePaymentMode,
      latestSendLockData?.stripePaymentUrl || editorForm.stripePaymentUrl,
      latestSendLockData?.stripePaymentReference || editorForm.stripePaymentReference,
    ),
    [
      editorForm.stripePaymentMode,
      editorForm.stripePaymentReference,
      editorForm.stripePaymentUrl,
      editorForm.stripeSurchargeEnabled,
      latestSendLockData?.stripePaymentMode,
      latestSendLockData?.stripePaymentReference,
      latestSendLockData?.stripePaymentUrl,
      latestSendLockData?.stripeSurchargeEnabled,
    ],
  );
  const latestVisibleStripePaymentUrl = useMemo(
    () => getVisibleStripePaymentUrl(editorForm.entity, latestSendLockData?.stripePaymentUrl || editorForm.stripePaymentUrl),
    [editorForm.entity, editorForm.stripePaymentUrl, latestSendLockData?.stripePaymentUrl],
  );
  const selectableClients = useMemo(() => {
    const seen = new Set<string>();
    return clientOptions
      .map((client) => ({
        ClientId: client.ClientId,
        Name: String(client.Name || '').trim(),
        Company: String(client.Company || '').trim(),
        Email: String(client.Email || '').trim(),
        Phone: formatPhoneForDisplay(client.Phone),
        Address: String(client.Address || '').trim(),
      }))
      .filter((client) => {
        const key = `${normalizeLookup(client.Name)}|${normalizeLookup(client.Company)}|${normalizeLookup(client.Email)}|${normalizeLookup(client.Phone)}`;
        if (!client.Name || seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      })
      .sort((a, b) => a.Name.localeCompare(b.Name));
  }, [clientOptions]);
  const hasClientSearchQuery = clientSearchQuery.trim().length > 0;
  const filteredSelectableClients = useMemo(() => {
    const query = normalizeLookup(clientSearchQuery);
    if (!query) {
      return [];
    }
    const mergedClients = [...searchedClientOptions, ...selectableClients];
    const seen = new Set<string>();
    const matches = mergedClients
      .filter((client) => {
        const dedupeKey = `${normalizeLookup(client.Name)}|${normalizeLookup(client.Company)}|${normalizeLookup(client.Email)}|${normalizeLookup(client.Phone)}`;
        if (!client.Name || seen.has(dedupeKey)) {
          return false;
        }
        seen.add(dedupeKey);
        if (!query) {
          return true;
        }
        return (
          normalizeLookup(client.Name).includes(query)
          || normalizeLookup(client.Company).includes(query)
          || normalizeLookup(client.Email).includes(query)
          || normalizeLookup(client.Phone).includes(query)
          || normalizeLookup(client.Address).includes(query)
        );
      });
    return matches.slice(0, 12);
  }, [clientSearchQuery, searchedClientOptions, selectableClients]);
  const clientSearchStatus = useMemo(() => {
    const query = clientSearchQuery.trim();
    if (!query) {
      return 'Type a client name, company, phone, email, or address to filter the saved client list.';
    }
    if (clientSearchBusy) {
      return 'Searching saved clients...';
    }
    if (filteredSelectableClients.length === 0) {
      return 'No saved clients match that search yet.';
    }
    return `Showing ${filteredSelectableClients.length} matching saved client${filteredSelectableClients.length === 1 ? '' : 's'}.`;
  }, [clientSearchBusy, clientSearchQuery, filteredSelectableClients.length]);
  const filteredInvoiceOptions = useMemo(() => {
    const query = invoiceLookup.trim().toLowerCase();
    if (!query) {
      return invoiceOptions.slice(0, 8);
    }
    return invoiceOptions
      .filter((invoice) =>
        String(invoice.InvoiceNumber || '').toLowerCase().includes(query)
        || String(invoice.Client || '').toLowerCase().includes(query)
        || String(invoice.Description || '').toLowerCase().includes(query)
      )
      .slice(0, 8);
  }, [invoiceLookup, invoiceOptions]);
  const pendingInvoiceOptions = useMemo(() => (
    invoiceOptions.filter((invoice) => isInvoicePendingToSend(invoice.Status))
  ), [invoiceOptions]);
  const filteredPendingInvoiceOptions = useMemo(() => {
    const query = finaliseLookup.trim().toLowerCase();
    if (!query) {
      return pendingInvoiceOptions.slice(0, 8);
    }
    return pendingInvoiceOptions
      .filter((invoice) => (
        String(invoice.InvoiceNumber || '').toLowerCase().includes(query)
        || String(invoice.Client || '').toLowerCase().includes(query)
        || String(invoice.Description || '').toLowerCase().includes(query)
      ))
      .slice(0, 8);
  }, [finaliseLookup, pendingInvoiceOptions]);
  const storedDraftLink = buildDraftContentLink(storedDraftPdf);
  const currentInvoiceStatus = String(latestSendLockData?.status || loadedInvoiceStatus || 'Draft').trim() || 'Draft';
  const currentRevisionNo = Number(latestSendLockData?.revisionNo || loadedRevisionNo || 1);
  const currentLatestIssuedRevisionNo = latestSendLockData?.supersededRevisionNo ?? loadedLatestIssuedRevisionNo ?? loadedSupersedesRevisionNo;
  const currentLatestIssuedRevisionStatus = loadedLatestIssuedRevisionStatus;
  const currentHasPendingRevision = Boolean(loadedHasPendingRevision || (currentLatestIssuedRevisionNo && currentRevisionNo > currentLatestIssuedRevisionNo && currentInvoiceStatus === 'Draft'));
  const currentRevisionCaption = currentHasPendingRevision && currentLatestIssuedRevisionNo
    ? `Revision ${currentRevisionNo} draft will replace ${currentLatestIssuedRevisionStatus || 'issued'} revision ${currentLatestIssuedRevisionNo}`
    : `Revision ${currentRevisionNo}`;
  const currentPaymentState = getInvoicePaymentState({
    Status: currentInvoiceStatus,
    PaymentStatus: editorForm.paymentStatus,
  });
  const sendRecipientEmails = useMemo(() => (
    buildEffectiveRecipientEmails(sendForm.recipientEmail, sendForm.additionalRecipientEmailsText)
  ), [sendForm.additionalRecipientEmailsText, sendForm.recipientEmail]);
  const paymentReference = String(editorForm.invoiceNumber || '').trim()
    || buildInvoicePaymentReference('', editorForm.rfiTicketNumber)
    || 'Invoice number pending';
  const invoiceSourceCaption = mode === 'amend'
    ? (currentHasPendingRevision && currentLatestIssuedRevisionNo
      ? `Editing a new draft revision for the previously ${String(currentLatestIssuedRevisionStatus || 'issued').toLowerCase()} invoice`
      : 'Editing a saved invoice draft')
    : 'Creating a new invoice draft';
  const selectedPendingInvoice = useMemo(() => {
    const activeInvoiceId = String(sendForm.invoiceId || editorForm.invoiceId || '').trim();
    const activeInvoiceNumber = normalizeLookup(editorForm.invoiceNumber || finaliseLookup);
    return pendingInvoiceOptions.find((invoice) => {
      const invoiceId = String(invoice.InvoiceId || '').trim();
      const invoiceNumber = normalizeLookup(invoice.InvoiceNumber);
      return (activeInvoiceId && invoiceId === activeInvoiceId) || (activeInvoiceNumber && invoiceNumber === activeInvoiceNumber);
    }) || null;
  }, [editorForm.invoiceId, editorForm.invoiceNumber, finaliseLookup, pendingInvoiceOptions, sendForm.invoiceId]);
  const selectedFinaliseInvoiceLabel = selectedPendingInvoice?.InvoiceNumber || editorForm.invoiceNumber || finaliseLookup || 'Not selected yet';
  useEffect(() => {
    setSendForm((prev) => ({
      ...prev,
      ccEmails: buildEffectiveCcEmails({
        recipientEmail: prev.recipientEmail,
        additionalRecipientEmailsText: prev.additionalRecipientEmailsText,
        additionalCcEmailsText: prev.additionalCcEmailsText,
        clientEmail: editorForm.clientEmail,
      }),
    }));
  }, [editorForm.clientEmail]);

  const updateSendRecipientEmail = (recipientEmail: string) => {
    setSendForm((prev) => ({
      ...prev,
      recipientEmail,
      ccEmails: buildEffectiveCcEmails({
        recipientEmail,
        additionalRecipientEmailsText: prev.additionalRecipientEmailsText,
        additionalCcEmailsText: prev.additionalCcEmailsText,
        clientEmail: editorForm.clientEmail,
      }),
    }));
  };

  const updateSendAdditionalRecipients = (additionalRecipientEmailsText: string) => {
    setSendForm((prev) => ({
      ...prev,
      additionalRecipientEmailsText,
      ccEmails: buildEffectiveCcEmails({
        recipientEmail: prev.recipientEmail,
        additionalRecipientEmailsText,
        additionalCcEmailsText: prev.additionalCcEmailsText,
        clientEmail: editorForm.clientEmail,
      }),
    }));
  };

  const updateSendAdditionalCc = (additionalCcEmailsText: string) => {
    setSendForm((prev) => ({
      ...prev,
      additionalCcEmailsText,
      ccEmails: buildEffectiveCcEmails({
        recipientEmail: prev.recipientEmail,
        additionalRecipientEmailsText: prev.additionalRecipientEmailsText,
        additionalCcEmailsText,
        clientEmail: editorForm.clientEmail,
      }),
    }));
  };

  useEffect(() => {
    const draft = readInvoiceWorkflowSessionDraft();
    if (draft?.editorForm) {
      setMode(draft.mode === 'amend' ? 'amend' : 'create');
      setActiveTab(draft.activeTab || 'sheet');
      setEditorForm(draft.editorForm);
      setSendForm(createSendForm({
        ...(draft.sendForm || {}),
        pdfFile: null,
      }));
      setQuickSendOpen(Boolean(draft.quickSendOpen));
      setFinaliseLookup(draft.finaliseLookup || '');
      setInvoiceLookup(draft.invoiceLookup || '');
      setProjectQuery(draft.projectQuery || draft.editorForm.projectNumber || '');
    }
    setWorkflowDraftRestored(true);
  }, []);

  useEffect(() => {
    if (!workflowDraftRestored) {
      return;
    }

    writeInvoiceWorkflowSessionDraft({
      savedAt: Date.now(),
      mode,
      activeTab,
      editorForm,
      sendForm: {
        invoiceId: sendForm.invoiceId,
        recipientEmail: sendForm.recipientEmail,
        additionalRecipientEmailsText: sendForm.additionalRecipientEmailsText,
        additionalCcEmailsText: sendForm.additionalCcEmailsText,
        ccEmails: sendForm.ccEmails,
      },
      quickSendOpen,
      finaliseLookup,
      invoiceLookup,
      projectQuery,
    });
  }, [activeTab, editorForm, finaliseLookup, invoiceLookup, mode, projectQuery, quickSendOpen, sendForm, workflowDraftRestored]);

  useEffect(() => {
    if (!quickSendOpen) {
      return;
    }

    const timer = window.setTimeout(() => {
      quickSendPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 40);

    return () => window.clearTimeout(timer);
  }, [quickSendOpen]);

  useEffect(() => {
    let active = true;

    const loadInitialData = async () => {
      try {
        const { azureSqlService } = await import('@/services/azureSqlService.js');
        const [loadedCategories, loadedInvoices, loadedClients, loadedQuotations] = await Promise.all([
          azureSqlService.getInvoiceWorkflowCategories(),
          azureSqlService.getInvoices({ page: 1, pageSize: 200 }),
          azureSqlService.getClients({ page: 1, pageSize: 200 }),
          azureSqlService.getQuotations({ page: 1, pageSize: 200 }),
        ]);

        if (!active) return;
        setCategories(Array.isArray(loadedCategories) ? loadedCategories : []);
        setInvoiceOptions(Array.isArray(loadedInvoices) ? loadedInvoices : []);
        setClientOptions(Array.isArray(loadedClients) ? loadedClients : []);
        setQuotationClientOptions(Array.isArray(loadedQuotations) ? loadedQuotations : []);
      } catch (err: any) {
        if (!active) return;
        setError(formatUserFacingError(
          err,
          'Invoice workflow data could not load.',
          'You can keep working on visible fields, then try loading the workflow data again.'
        ));
      }
    };

    loadInitialData();
    return () => {
      active = false;
    };
  }, []);

  const refreshInvoiceOptions = async () => {
    const { azureSqlService } = await import('@/services/azureSqlService.js');
    const loadedInvoices = await azureSqlService.getInvoices({ page: 1, pageSize: 200, useCache: false });
    setInvoiceOptions(Array.isArray(loadedInvoices) ? loadedInvoices : []);
  };

  useEffect(() => {
    if (mode !== 'create') return undefined;
    const query = projectQuery.trim();
    if (query.length < 2) {
      setProjectOptions([]);
      return undefined;
    }

    let active = true;
    const timer = window.setTimeout(async () => {
      try {
        const { azureSqlService } = await import('@/services/azureSqlService.js');
        const matches = await azureSqlService.searchProjects(query, 8, { lifecycle: 'all' });
        if (active) {
          setProjectOptions(Array.isArray(matches) ? matches : []);
        }
      } catch {
        if (active) {
          setProjectOptions([]);
        }
      }
    }, 180);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [mode, projectQuery]);

  useEffect(() => {
    const query = clientSearchQuery.trim();
    if (query.length < 2) {
      setSearchedClientOptions([]);
      setClientSearchBusy(false);
      return undefined;
    }

    let active = true;
    setClientSearchBusy(true);
    const timer = window.setTimeout(async () => {
      try {
        const { azureSqlService } = await import('@/services/azureSqlService.js');
        const matches = await azureSqlService.searchClients(query, 12);
        if (!active) {
          return;
        }
        const normalizedMatches = Array.isArray(matches)
          ? matches.map((client: any) => ({
            ClientId: client?.ClientId ?? undefined,
            Name: String(client?.Name || client?.Client || '').trim(),
            Company: String(client?.Company || client?.ClientCompany || client?.CompanyName || '').trim(),
            Email: String(client?.Email || client?.ClientEmail || '').trim(),
            Phone: formatPhoneForDisplay(client?.Phone || client?.ClientMobile || client?.Mobile || ''),
            Address: String(client?.Address || client?.ClientAddress || '').trim(),
          })).filter((client: ClientOption) => Boolean(client.Name))
          : [];
        setSearchedClientOptions(normalizedMatches);
      } catch {
        if (!active) {
          return;
        }
        setSearchedClientOptions([]);
      } finally {
        if (active) {
          setClientSearchBusy(false);
        }
      }
    }, 220);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [clientSearchQuery]);

  useEffect(() => {
    const state = (location.state || {}) as InvoiceWorkflowPrefillState;
    if (state.prefill) {
      quotationHydrationRequestRef.current += 1;
      clearInvoiceWorkflowSessionDraft();
      const nextForm = mapPrefillToForm(state.prefill);
      setMode(state.mode === 'amend' ? 'amend' : 'create');
      setActiveTab('sheet');
      setEditorForm(nextForm);
      setSelectedClientId('');
      setClientSearchQuery('');
      setSearchedClientOptions([]);
      setPostSavePrompt(null);
      setInvoiceSource(null);
      setLoadedInvoiceStatus('Draft');
      setLoadedRevisionNo(1);
      setLoadedLatestIssuedRevisionNo(null);
      setLoadedLatestIssuedRevisionStatus(null);
      setLoadedHasPendingRevision(false);
      setLoadedSupersedesRevisionNo(null);
      setStoredDraftPdf(null);
      setSendLockResult(null);
      setEmailPreview(null);
      setEmailComposerDraft(null);
      setSendForm(createSendForm({
        invoiceId: '',
        recipientEmail: nextForm.clientEmail || '',
        ccEmails: buildEffectiveCcEmails({
          recipientEmail: nextForm.clientEmail || '',
          clientEmail: nextForm.clientEmail,
        }),
      }));
      setProjectQuery(nextForm.projectNumber || '');
      setInvoiceLookup(nextForm.invoiceNumber || '');
      setSuccess(state.prefill.successMessage || `Prepared invoice draft ${nextForm.invoiceNumber || 'for editing'}.`);
      return;
    }

    if (!state.invoiceId && !state.invoiceNumber) {
      return;
    }

    clearInvoiceWorkflowSessionDraft();
    setMode(state.mode === 'create' ? 'create' : 'amend');
    loadDraft({
      invoiceId: state.invoiceId ? String(state.invoiceId) : undefined,
      invoiceNumber: state.invoiceNumber,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab !== 'finalise') {
      return;
    }

    if (String(sendForm.invoiceId || '').trim()) {
      return;
    }

    if (String(editorForm.invoiceId || '').trim()) {
      setSendForm((prev) => ({
        ...prev,
        invoiceId: String(editorForm.invoiceId || '').trim(),
        recipientEmail: prev.recipientEmail || editorForm.clientEmail || '',
        ccEmails: buildEffectiveCcEmails({
          recipientEmail: prev.recipientEmail || editorForm.clientEmail || '',
          additionalRecipientEmailsText: prev.additionalRecipientEmailsText,
          additionalCcEmailsText: prev.additionalCcEmailsText,
          clientEmail: editorForm.clientEmail,
        }),
      }));
      return;
    }

    const firstPending = pendingInvoiceOptions[0];
    if (!firstPending?.InvoiceId) {
      return;
    }

    setSendForm((prev) => ({
      ...prev,
      invoiceId: String(firstPending.InvoiceId),
      recipientEmail: prev.recipientEmail,
      ccEmails: prev.ccEmails,
    }));
    setFinaliseLookup(firstPending.InvoiceNumber || '');
  }, [activeTab, editorForm.clientEmail, editorForm.invoiceId, pendingInvoiceOptions, sendForm.invoiceId]);

  const clearFeedback = () => {
    setError(null);
    setSuccess(null);
  };

  const finaliseSavedInvoice = (prompt: PostSavePrompt | null = postSavePrompt) => {
    if (!prompt) return;
    setPostSavePrompt(null);
    setFinaliseLookup(prompt.invoiceNumber);
    void loadDraft({
      invoiceId: prompt.invoiceId,
      invoiceNumber: prompt.invoiceNumber,
      targetTab: 'finalise',
      suppressSuccess: true,
    });
  };

  const buildInvoicePayload = (form: EditorForm, includeInvoiceId = false) => ({
    invoiceId: includeInvoiceId ? form.invoiceId || undefined : undefined,
    invoiceType: form.invoiceType === 'inspection' ? 'inspection' : undefined,
    inspectionJobId: form.inspectionJobId || undefined,
    inspectionAddress: form.invoiceType === 'inspection' ? (form.projectAddress || form.clientAddress || undefined) : undefined,
    invoiceNumber: form.invoiceNumber || undefined,
    projectId: form.invoiceType === 'inspection' ? undefined : (form.projectId || undefined),
    projectNumber: form.invoiceType === 'inspection' ? undefined : (form.projectNumber || undefined),
    taskId: form.invoiceType === 'inspection' ? undefined : (form.taskId || undefined),
    rfiTicketNumber: form.rfiTicketNumber || undefined,
    pricingCurrency: 'AUD',
    entity: form.entity,
    clientName: form.clientName || undefined,
    clientCompany: form.clientCompany || undefined,
    clientAddress: form.clientAddress || undefined,
    clientMobile: normalizePhoneForStorage(form.clientMobile) || undefined,
    clientEmail: form.clientEmail || undefined,
    projectAddress: form.projectAddress || undefined,
    invoiceDate: form.invoiceDate || undefined,
    dueDate: form.dueDate || undefined,
    notes: form.notes || undefined,
    stripePaymentMode: canManageStripePaymentLinks && canInvoiceEntityUseStripe(form.entity) ? (form.stripePaymentMode || undefined) : 'none',
    stripeSurchargeEnabled: canManageStripePaymentLinks && canInvoiceEntityUseStripe(form.entity)
      ? (form.stripePaymentMode === 'payment_link' && isStripeSurchargeAllowed() ? form.stripeSurchargeEnabled : false)
      : false,
    items: buildPayloadItems(form.items),
  });

  const syncSavedInvoiceState = (form: EditorForm, response: any) => {
    const responseInvoiceId = String(response?.invoiceId || response?.data?.invoiceId || form.invoiceId || '');
    const responseInvoiceNumber = String(response?.invoiceNumber || response?.data?.invoiceNumber || form.invoiceNumber || '');
    const responseProjectNumber = String(response?.projectNumber || response?.data?.projectNumber || form.projectNumber || '');
    const responseInspectionJob = response?.inspectionJob || response?.data?.inspectionJob || null;
    const nextForm = {
      ...form,
      invoiceType: String(response?.invoiceType || response?.data?.invoiceType || form.invoiceType).toLowerCase() === 'inspection' ? 'inspection' as const : 'standard' as const,
      inspectionJobId: String(response?.inspectionJobId || response?.data?.inspectionJobId || responseInspectionJob?.inspectionJobId || form.inspectionJobId || ''),
      inspectionFolderPath: String(responseInspectionJob?.sharePointFolderPath || form.inspectionFolderPath || ''),
      inspectionFolderUrl: String(responseInspectionJob?.sharePointFolderUrl || form.inspectionFolderUrl || ''),
      invoiceId: responseInvoiceId,
      invoiceNumber: responseInvoiceNumber,
      projectNumber: responseProjectNumber,
      taskId: String(response?.taskId || response?.data?.taskId || form.taskId || ''),
      rfiTicketNumber: String(response?.rfiTicketNumber || response?.data?.rfiTicketNumber || form.rfiTicketNumber || ''),
      stripePaymentMode: normalizeStripePaymentMode(
        String(response?.stripePaymentMode || response?.data?.stripePaymentMode || ''),
        String(response?.stripePaymentUrl || response?.data?.stripePaymentUrl || form.stripePaymentUrl || ''),
        String(response?.stripePaymentReference || response?.data?.stripePaymentReference || form.stripePaymentReference || ''),
      ),
      stripeSurchargeEnabled: normalizeStripeSurchargeEnabled(
        response?.stripeSurchargeEnabled ?? response?.data?.stripeSurchargeEnabled ?? form.stripeSurchargeEnabled,
        String(response?.stripePaymentMode || response?.data?.stripePaymentMode || ''),
        String(response?.stripePaymentUrl || response?.data?.stripePaymentUrl || form.stripePaymentUrl || ''),
        String(response?.stripePaymentReference || response?.data?.stripePaymentReference || form.stripePaymentReference || ''),
      ),
      stripePaymentUrl: String(response?.stripePaymentUrl || response?.data?.stripePaymentUrl || form.stripePaymentUrl || ''),
      stripePaymentReference: String(response?.stripePaymentReference || response?.data?.stripePaymentReference || form.stripePaymentReference || ''),
      paymentStatus: String(response?.paymentStatus || response?.data?.paymentStatus || form.paymentStatus || 'Unpaid'),
    };

    setInvoiceSource((response?.source || response?.data?.source || invoiceSource) as InvoiceSource | null);
    setSendForm((prev) => ({
      ...prev,
      invoiceId: responseInvoiceId,
      recipientEmail: prev.recipientEmail || nextForm.clientEmail || '',
      ccEmails: buildEffectiveCcEmails({
        recipientEmail: prev.recipientEmail || nextForm.clientEmail || '',
        additionalRecipientEmailsText: prev.additionalRecipientEmailsText,
        additionalCcEmailsText: prev.additionalCcEmailsText,
        clientEmail: nextForm.clientEmail,
      }),
      pdfFile: null,
    }));
    setEditorForm(nextForm);
    return nextForm;
  };

  const persistInvoiceForOutput = async (): Promise<PersistInvoiceForOutputResult> => {
    const normalizedInvoiceId = String(editorForm.invoiceId || '').trim();
    const refreshedForm = refreshInvoiceDates(editorForm);

    if (!normalizedInvoiceId) {
      setEditorForm(refreshedForm);
      return {
        form: refreshedForm,
        locked: false,
      };
    }

    const { azureSqlService } = await import('@/services/azureSqlService.js');
    try {
      const response = await azureSqlService.amendWorkflowInvoice(buildInvoicePayload(refreshedForm, true));
      return {
        form: syncSavedInvoiceState(refreshedForm, response),
        locked: false,
      };
    } catch (err: any) {
      if (isInvoiceLockedAfterSendError(err)) {
        return {
          form: editorForm,
          locked: true,
        };
      }
      throw err;
    }
  };

  const loadDraft = async ({
    invoiceId,
    invoiceNumber,
    targetTab = 'sheet',
    suppressSuccess = false,
  }: LoadDraftOptions) => {
    const normalizedId = String(invoiceId || '').trim();
    const normalizedNumber = String(invoiceNumber || '').trim();
    if (!normalizedId && !normalizedNumber) {
      setError('Enter an invoice number or select an invoice to load.');
      return null;
    }

    nextInvoiceNumberRequestRef.current += 1;
    quotationHydrationRequestRef.current += 1;
    setBusy('load-draft');
    clearFeedback();
    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const draft = await azureSqlService.getWorkflowInvoiceDraft({
        invoiceId: normalizedId || undefined,
        invoiceNumber: normalizedNumber || undefined,
      }) as InvoiceDraftResponse;

      const nextForm = mapDraftToForm(draft);
      setEditorForm(nextForm);
      setSelectedClientId('');
      setClientSearchQuery('');
      setSearchedClientOptions([]);
      setPostSavePrompt(null);
      setMode('amend');
      setActiveTab(targetTab);
      setInvoiceLookup(nextForm.invoiceNumber);
      setInvoiceSource(draft.source || null);
      setLoadedInvoiceStatus(String(draft.status || 'Draft'));
      setLoadedRevisionNo(Number(draft.revisionNo || 1));
      setLoadedLatestIssuedRevisionNo(typeof draft.latestIssuedRevisionNo === 'number' ? draft.latestIssuedRevisionNo : null);
      setLoadedLatestIssuedRevisionStatus(draft.latestIssuedRevisionStatus || null);
      setLoadedHasPendingRevision(Boolean(draft.hasPendingRevision));
      setLoadedSupersedesRevisionNo(typeof draft.supersedesRevisionNo === 'number' ? draft.supersedesRevisionNo : null);
      setStoredDraftPdf(draft.storedDraftPdf || null);
      setSendLockResult(null);
      setEmailPreview(null);
      setEmailComposerDraft(null);
      setSendForm((prev) => ({
        ...prev,
        invoiceId: nextForm.invoiceId,
        recipientEmail: draft.recipientEmail || nextForm.clientEmail || '',
        additionalRecipientEmailsText: '',
        additionalCcEmailsText: deriveManualCcEmailsText(draft.ccEmails, nextForm.clientEmail),
        ccEmails: Array.isArray(draft.ccEmails)
          ? parseEmailListInput(draft.ccEmails)
          : buildEffectiveCcEmails({
            recipientEmail: draft.recipientEmail || nextForm.clientEmail || '',
            clientEmail: nextForm.clientEmail,
          }),
        pdfFile: null,
      }));
      setProjectQuery(nextForm.projectNumber || '');
      if (!suppressSuccess) {
        setSuccess(`Loaded invoice ${nextForm.invoiceNumber || normalizedId} into the editor.`);
      }
      return {
        form: nextForm,
        draft,
      };
    } catch (err: any) {
      setError(formatUserFacingError(err, 'Invoice draft could not load.'));
      return null;
    } finally {
      setBusy(null);
    }
  };

  const populateNextInvoiceNumber = async (project: { projectId?: string; projectNumber?: string }) => {
    const normalizedProjectNumber = String(project.projectNumber || '').trim();
    if (!normalizedProjectNumber) return;
    const requestId = ++nextInvoiceNumberRequestRef.current;

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const response = await azureSqlService.getWorkflowInvoiceNextNumber({
        projectId: project.projectId,
        projectNumber: normalizedProjectNumber,
      });
      if (requestId !== nextInvoiceNumberRequestRef.current) {
        return;
      }
      const responseProjectId = String(response?.projectId ?? response?.data?.projectId ?? project.projectId ?? '');
      const responseProjectNumber = String(response?.projectNumber || response?.data?.projectNumber || normalizedProjectNumber);
      setInvoiceSource(((response?.source || response?.data?.source) as InvoiceSource) || null);
      setEditorForm((prev) => ({
        ...prev,
        projectId: responseProjectId || prev.projectId,
        projectNumber: responseProjectNumber || prev.projectNumber,
        invoiceNumber: response?.nextInvoiceNumber || response?.data?.nextInvoiceNumber || prev.invoiceNumber,
      }));
    } catch (err: any) {
      if (requestId === nextInvoiceNumberRequestRef.current) {
        setError(formatUserFacingError(err, 'Invoice number could not be generated.'));
      }
    }
  };

  const applyClientSelection = (
    nextValues: Partial<Pick<EditorForm, 'clientName' | 'clientCompany' | 'clientAddress' | 'clientMobile' | 'clientEmail'>>,
    replaceExisting = true,
  ) => {
    setEditorForm((prev) => mergeInvoiceClientDetails(prev, nextValues, replaceExisting));
    if (nextValues.clientEmail) {
      setSendForm((prev) => ({
        ...prev,
        recipientEmail: replaceExisting || !prev.recipientEmail.trim() ? nextValues.clientEmail || '' : prev.recipientEmail,
        ccEmails: buildEffectiveCcEmails({
          recipientEmail: replaceExisting || !prev.recipientEmail.trim() ? nextValues.clientEmail || '' : prev.recipientEmail,
          additionalRecipientEmailsText: prev.additionalRecipientEmailsText,
          additionalCcEmailsText: prev.additionalCcEmailsText,
          clientEmail: nextValues.clientEmail || editorForm.clientEmail,
        }),
      }));
    }
  };

  const findQuotationClientMatch = (projectNumber: string) => {
    const normalizedProjectNumber = normalizeLookup(projectNumber);
    if (!normalizedProjectNumber) {
      return null;
    }

    return quotationClientOptions.find((quotation) => (
      normalizeLookup(quotation.QuotationNumber) === normalizedProjectNumber
    )) || null;
  };

  const hydrateFromQuotation = async (projectNumber: string, replaceExisting = false) => {
    const normalizedProjectNumber = String(projectNumber || '').trim();
    const requestId = ++quotationHydrationRequestRef.current;
    if (!normalizedProjectNumber) {
      return null;
    }

    let matchedQuotation = findQuotationClientMatch(normalizedProjectNumber);

    if (!matchedQuotation) {
      try {
        const { azureSqlService } = await import('@/services/azureSqlService.js');
        const freshMatches = await azureSqlService.getQuotations({
          page: 1,
          pageSize: 10,
          query: { q: normalizedProjectNumber },
          useCache: false,
        }) as QuotationClientOption[];

        if (requestId !== quotationHydrationRequestRef.current) {
          return null;
        }

        if (Array.isArray(freshMatches) && freshMatches.length > 0) {
          setQuotationClientOptions((prev) => {
            const merged = [...freshMatches, ...prev];
            const seen = new Set<string>();
            return merged.filter((quotation) => {
              const key = normalizeLookup(quotation.QuotationId || quotation.QuotationNumber);
              if (!key || seen.has(key)) {
                return false;
              }
              seen.add(key);
              return true;
            });
          });

          matchedQuotation = freshMatches.find((quotation) => (
            normalizeLookup(quotation.QuotationNumber) === normalizeLookup(normalizedProjectNumber)
          )) || null;
        }
      } catch {
        // Leave the form editable even if quotation lookup is unavailable.
      }
    }

    if (!matchedQuotation) {
      return null;
    }

    if (requestId !== quotationHydrationRequestRef.current) {
      return null;
    }

    applyClientSelection({
      clientName: matchedQuotation.ClientName,
      clientCompany: matchedQuotation.CompanyName,
      clientAddress: matchedQuotation.ClientAddress,
      clientMobile: matchedQuotation.ClientMobile,
      clientEmail: matchedQuotation.ClientEmail,
    }, replaceExisting);

    const enrichedProjectAddress = formatProjectAddressParts(
      matchedQuotation.Address,
      matchedQuotation.Suburb,
      matchedQuotation.PostCode,
    ) || String(matchedQuotation.ProjectLocation || '').trim();

    if (enrichedProjectAddress) {
      setEditorForm((prev) => ({
        ...prev,
        projectAddress: replaceExisting || !prev.projectAddress.trim() ? enrichedProjectAddress : prev.projectAddress,
      }));
    }

    return matchedQuotation;
  };

  const selectProject = (project: ProjectOption) => {
    const nextProjectNumber = project.ProjectCode || '';
    const nextProjectId = String(project.ProjectId || '');
    const nextProjectAddress = formatProjectAddressParts(project.SiteAddress, project.Suburb, project.PostCode);
    const shouldReplaceClient = !String(editorForm.projectId || '').trim()
      || String(editorForm.projectId) !== nextProjectId
      || normalizeLookup(editorForm.projectNumber) !== normalizeLookup(nextProjectNumber);
    setProjectQuery(nextProjectNumber);
    setProjectOptions([]);
    setEditorForm((prev) => ({
      ...prev,
      projectId: nextProjectId,
      projectNumber: nextProjectNumber,
      projectAddress: nextProjectAddress || project.SiteAddress || prev.projectAddress,
    }));
    clearFeedback();
    populateNextInvoiceNumber({
      projectId: nextProjectId,
      projectNumber: nextProjectNumber,
    });
    void hydrateFromQuotation(nextProjectNumber, shouldReplaceClient);
  };

  const handleProjectQueryChange = (value: string) => {
    nextInvoiceNumberRequestRef.current += 1;
    quotationHydrationRequestRef.current += 1;
    setProjectQuery(value);
    if (mode !== 'create') {
      return;
    }

    setEditorForm((prev) => {
      const previousProjectNumber = normalizeLookup(prev.projectNumber);
      const nextProjectNumber = normalizeLookup(value);
      return {
        ...prev,
        projectId: previousProjectNumber && previousProjectNumber === nextProjectNumber ? prev.projectId : '',
        projectNumber: value,
      };
    });
    setInvoiceSource(null);
  };

  const commitProjectLookup = (value: string) => {
    if (mode !== 'create') {
      return;
    }

    const normalizedProjectNumber = String(value || '').trim();
    if (!normalizedProjectNumber) {
      return;
    }

    const exactProject = projectOptions.find((project) => (
      normalizeLookup(project.ProjectCode) === normalizeLookup(normalizedProjectNumber)
    ));
    if (exactProject) {
      selectProject(exactProject);
      return;
    }

    const shouldReplaceClient = !String(editorForm.projectId || '').trim()
      || normalizeLookup(editorForm.projectNumber) !== normalizeLookup(normalizedProjectNumber);

    setProjectQuery(normalizedProjectNumber);
    setProjectOptions([]);
    setEditorForm((prev) => ({
      ...prev,
      projectId: '',
      projectNumber: normalizedProjectNumber,
    }));
    populateNextInvoiceNumber({ projectNumber: normalizedProjectNumber });
    void hydrateFromQuotation(normalizedProjectNumber, shouldReplaceClient);
  };

  const updateForm = (field: EditableInvoiceField, value: string) => {
    setEditorForm((prev) => {
      if (field === 'stripePaymentMode') {
        const nextMode = normalizeStripePaymentMode(value, prev.stripePaymentUrl, prev.stripePaymentReference);
        return {
          ...prev,
          stripePaymentMode: nextMode,
          stripeSurchargeEnabled: nextMode === 'payment_link'
            ? ((prev.stripePaymentMode === 'payment_link' || prev.stripePaymentUrl || prev.stripePaymentReference)
              ? (isStripeSurchargeAllowed() ? prev.stripeSurchargeEnabled : false)
              : getDefaultStripeSurchargeEnabled())
            : prev.stripeSurchargeEnabled,
          stripePaymentUrl: nextMode === 'none' ? '' : prev.stripePaymentUrl,
          stripePaymentReference: nextMode === 'none' ? '' : prev.stripePaymentReference,
          paymentStatus: nextMode === 'none' && prev.paymentStatus !== 'Paid' ? 'Unpaid' : prev.paymentStatus,
        };
      }

      if (field === 'entity') {
        const nextEntity = value;
        if (!canInvoiceEntityUseStripe(nextEntity)) {
          return {
            ...prev,
            entity: nextEntity,
            stripePaymentMode: 'none',
            stripePaymentUrl: '',
            stripePaymentReference: '',
            paymentStatus: prev.paymentStatus !== 'Paid' ? 'Unpaid' : prev.paymentStatus,
          };
        }

        return { ...prev, entity: nextEntity };
      }

      return { ...prev, [field]: value };
    });
  };

  const updateStripeSurchargeEnabled = (enabled: boolean) => {
    setEditorForm((prev) => ({
      ...prev,
      stripeSurchargeEnabled: isStripeSurchargeAllowed() ? enabled : false,
    }));
  };

  const formatEditorPhoneField = (field: 'clientMobile') => {
    setEditorForm((prev) => ({
      ...prev,
      [field]: normalizePhoneForStorage(prev[field]) || '',
    }));
  };

  const applySelectedClient = (selectedClient: ClientOption) => {
    setSelectedClientId(String(selectedClient.ClientId || ''));
    applyClientSelection({
      clientName: selectedClient.Name,
      clientCompany: selectedClient.Company,
      clientAddress: selectedClient.Address,
      clientMobile: selectedClient.Phone,
      clientEmail: selectedClient.Email,
    }, true);
    setClientSearchQuery(selectedClient.Name || '');
    setSearchedClientOptions([]);
  };

  const selectClientFromDirectory = (clientId: string) => {
    setSelectedClientId(clientId);
    const selectedClient = selectableClients.find((client) => String(client.ClientId || '') === clientId);
    if (!selectedClient) {
      return;
    }
    applySelectedClient(selectedClient);
  };

  const updateItem = (index: number, field: keyof ItemRow, value: string) => {
    setEditorForm((prev) => {
      const items = [...prev.items];
      const nextItem = { ...items[index], [field]: value };

      if (field === 'categoryId') {
        const selected = categories.find((category) => String(category.CategoryId) === value);
        if (selected) {
          nextItem.categoryName = selected.CategoryName || '';
          if (!nextItem.description.trim()) {
            nextItem.description = selected.DefaultDescription || selected.CategoryName || '';
          }
        }
      }

      items[index] = nextItem;
      return { ...prev, items };
    });
  };

  const addItemRow = () => {
    setEditorForm((prev) => ({ ...prev, items: [...prev.items, emptyRow()] }));
  };

  const removeItemRow = (index: number) => {
    setEditorForm((prev) => {
      const items = prev.items.filter((_, rowIndex) => rowIndex !== index);
      return { ...prev, items: items.length > 0 ? items : [emptyRow()] };
    });
  };

  const resetForCreate = () => {
    nextInvoiceNumberRequestRef.current += 1;
    quotationHydrationRequestRef.current += 1;
    clearInvoiceWorkflowSessionDraft();
    setMode('create');
    setActiveTab('sheet');
    setEditorForm(emptyForm());
    setSelectedClientId('');
    setPostSavePrompt(null);
    setProjectQuery('');
    setProjectOptions([]);
    setInvoiceLookup('');
    setFinaliseLookup('');
    setInvoiceSource(null);
    setLoadedInvoiceStatus('Draft');
    setLoadedRevisionNo(1);
    setLoadedLatestIssuedRevisionNo(null);
    setLoadedLatestIssuedRevisionStatus(null);
    setLoadedHasPendingRevision(false);
    setLoadedSupersedesRevisionNo(null);
    setStoredDraftPdf(null);
    setSendLockResult(null);
    setEmailPreview(null);
    setEmailComposerDraft(null);
    setSendForm(createSendForm());
    clearFeedback();
  };

  const startInspectionInvoice = () => {
    nextInvoiceNumberRequestRef.current += 1;
    quotationHydrationRequestRef.current += 1;
    setMode('create');
    setActiveTab('sheet');
    setEditorForm({
      ...emptyForm(),
      invoiceType: 'inspection',
      projectAddress: '',
      notes: 'Adhoc inspection invoice',
    });
    setSelectedClientId('');
    setPostSavePrompt(null);
    setProjectQuery('');
    setProjectOptions([]);
    setInvoiceLookup('');
    setFinaliseLookup('');
    setInvoiceSource(null);
    setLoadedInvoiceStatus('Draft');
    setLoadedRevisionNo(1);
    setLoadedLatestIssuedRevisionNo(null);
    setLoadedLatestIssuedRevisionStatus(null);
    setLoadedHasPendingRevision(false);
    setLoadedSupersedesRevisionNo(null);
    setStoredDraftPdf(null);
    setSendLockResult(null);
    setEmailPreview(null);
    setEmailComposerDraft(null);
    setSendForm(createSendForm());
    clearFeedback();
  };

  const useClientAddressForInspection = () => {
    setEditorForm((prev) => ({
      ...prev,
      projectAddress: prev.clientAddress || prev.projectAddress,
    }));
  };

  const createInspectionFolder = async () => {
    clearFeedback();
    const invoiceId = String(editorForm.invoiceId || sendForm.invoiceId || '').trim();
    if (!invoiceId) {
      setError('Save the inspection invoice draft before creating the SharePoint folder.');
      return;
    }
    if (!isInspectionInvoice) {
      setError('Switch to an inspection invoice before creating an inspection folder.');
      return;
    }

    setBusy('inspection-folder');
    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const response = await azureSqlService.createInspectionInvoiceFolder({
        invoiceId,
        inspectionJobId: editorForm.inspectionJobId || undefined,
        invoiceNumber: editorForm.invoiceNumber || undefined,
      });
      const inspectionJob = response?.inspectionJob || response?.data?.inspectionJob || null;
      setEditorForm((prev) => ({
        ...prev,
        inspectionJobId: String(response?.inspectionJobId || response?.data?.inspectionJobId || inspectionJob?.inspectionJobId || prev.inspectionJobId || ''),
        inspectionFolderPath: String(inspectionJob?.sharePointFolderPath || prev.inspectionFolderPath || ''),
        inspectionFolderUrl: String(inspectionJob?.sharePointFolderUrl || prev.inspectionFolderUrl || ''),
      }));
      setSuccess(`Created inspection SharePoint folder for ${response?.invoiceNumber || response?.data?.invoiceNumber || editorForm.invoiceNumber}.`);
    } catch (err: any) {
      setError(formatUserFacingError(err, 'Inspection SharePoint folder could not be created.'));
    } finally {
      setBusy(null);
    }
  };

  const renderStoreAndOpenDraftPdf = async (
    invoiceId: string,
    formOverride?: EditorForm,
    previewWindow?: Window | null,
  ) => {
    const targetForm = formOverride || editorForm;
    const normalizedInvoiceId = String(invoiceId || '').trim();
    if (!normalizedInvoiceId) {
      throw new Error('Invoice ID is required before rendering a draft PDF.');
    }

    const { azureSqlService } = await import('@/services/azureSqlService.js');
    const { pdfService } = await import('@/services/pdfService');
    const pdfData = buildInvoicePdfPayload(targetForm);
    const pdfBlob = await pdfService.generateInvoicePDFBlob(pdfData);
    const fileName = `${targetForm.invoiceNumber || normalizedInvoiceId}.pdf`;

    const uploaded = await azureSqlService.uploadDraftFile({
      entityType: 'invoice-pdf-draft',
      recordId: normalizedInvoiceId,
      file: pdfBlob,
      contentType: 'application/pdf',
      fileName,
    });

    const linked = await azureSqlService.linkWorkflowInvoiceDraftPdf({
      invoiceId: normalizedInvoiceId,
      draftFileId: uploaded?.fileId,
      pdfFileName: fileName,
      pdfContentType: 'application/pdf',
      contentPath: uploaded?.contentPath,
    });

    const nextStoredDraft = linked?.storedDraftPdf || linked?.data?.storedDraftPdf || null;
    setStoredDraftPdf(nextStoredDraft);
    openPdfBlob(pdfBlob, previewWindow);
    return nextStoredDraft;
  };

  const reviewStoredDraftPdf = async () => {
    clearFeedback();
    setBusy('review-draft');
    try {
      const activeInvoiceId = String(sendForm.invoiceId || editorForm.invoiceId || '').trim();
      const activeInvoiceNumber = String(finaliseLookup || editorForm.invoiceNumber || '').trim();
      let resolvedStoredDraft = storedDraftPdf;

      if (!resolvedStoredDraft?.draftFileId && (activeInvoiceId || activeInvoiceNumber)) {
        const refreshed = await loadDraft({
          invoiceId: activeInvoiceId || undefined,
          invoiceNumber: activeInvoiceNumber || undefined,
          targetTab: 'finalise',
          suppressSuccess: true,
        });
        resolvedStoredDraft = refreshed?.draft?.storedDraftPdf || null;
      }

      if (!resolvedStoredDraft?.draftFileId) {
        setError('No stored draft PDF is linked to this invoice yet. Render and preview the draft first.');
        return;
      }

      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const download = await azureSqlService.downloadDraftFile(resolvedStoredDraft.draftFileId);
      openPdfBlob(download.blob);
    } catch (err: any) {
      setError(formatUserFacingError(err, 'Stored draft PDF could not open.'));
    } finally {
      setBusy(null);
    }
  };

  const previewRenderedDraftPdf = async () => {
    clearFeedback();

    if (!hasInvoiceIdentity(editorForm)) {
      setError('Project number or invoice no. is required before previewing the PDF.');
      return;
    }

    if (!isInspectionInvoice && totals.usedItems.length === 0) {
      setError('Add at least one invoice line item before previewing the PDF.');
      return;
    }

    setBusy('preview-draft');
    const previewWindow = openPreviewWindow('Rendering invoice PDF preview...');
    try {
      const invoiceId = String(editorForm.invoiceId || sendForm.invoiceId || '').trim();
      const outputForm = refreshInvoiceDates(editorForm);
      const returnTab = activeTab;

      if (invoiceId) {
        const { form: persistedForm, locked } = await persistInvoiceForOutput();
        if (locked) {
          const { pdfService } = await import('@/services/pdfService');
          const pdfBlob = await pdfService.generateInvoicePDFBlob(buildInvoicePdfPayload(persistedForm));
          openPdfBlob(pdfBlob, previewWindow);
          setSuccess(
            storedDraftPdf?.draftFileId
              ? 'This invoice is already filed or sent, so a fresh preview was rendered from the current form without replacing the stored draft.'
              : 'This invoice is already filed or sent, so the PDF was previewed without saving a new draft.'
          );
        } else {
          await renderStoreAndOpenDraftPdf(invoiceId, persistedForm, previewWindow);
          await loadDraft({ invoiceId });
          setActiveTab(returnTab === 'finalise' ? 'finalise' : 'sheet');
          setSuccess('Refreshed invoice dates, stored the latest draft PDF, and opened it for review.');
        }
      } else {
        setEditorForm(outputForm);
        const { pdfService } = await import('@/services/pdfService');
        const pdfBlob = await pdfService.generateInvoicePDFBlob(buildInvoicePdfPayload(outputForm));
        openPdfBlob(pdfBlob, previewWindow);
        setSuccess('Refreshed the invoice dates and opened a rendered PDF preview. Save the invoice first if you want this draft stored and linked.');
      }
    } catch (err: any) {
      if (previewWindow && !previewWindow.closed) {
        previewWindow.close();
      }
      setError(formatUserFacingError(err, 'Invoice PDF preview could not open.'));
    } finally {
      setBusy(null);
    }
  };

  const openPendingInvoiceForFinalise = async (invoice: InvoiceOption) => {
    const invoiceId = invoice.InvoiceId ? String(invoice.InvoiceId) : '';
    const invoiceNumber = invoice.InvoiceNumber || '';
    setFinaliseLookup(invoiceNumber);
    setSendLockResult(null);
    setSendForm((prev) => ({
      ...prev,
      invoiceId: invoiceId || prev.invoiceId,
      pdfFile: null,
    }));
      await loadDraft({
        invoiceId: invoiceId || undefined,
        invoiceNumber: invoiceNumber || undefined,
        targetTab: 'finalise',
        suppressSuccess: true,
      });
    };

  const openQuickFinalisePanel = async (targetTab: WorkflowTab = activeTab) => {
    clearFeedback();
    const invoiceId = String(editorForm.invoiceId || '').trim();
    if (!invoiceId) {
      setError('Save the invoice first before finalising or sending it from the bottom shortcut.');
      return;
    }

    setSendForm((prev) => ({
      ...prev,
      invoiceId,
      recipientEmail:
        String(prev.invoiceId || '').trim() === invoiceId
          ? (prev.recipientEmail || editorForm.clientEmail || '')
          : (editorForm.clientEmail || ''),
      additionalRecipientEmailsText:
        String(prev.invoiceId || '').trim() === invoiceId
          ? prev.additionalRecipientEmailsText
          : '',
      additionalCcEmailsText:
        String(prev.invoiceId || '').trim() === invoiceId
          ? prev.additionalCcEmailsText
          : '',
      ccEmails: buildEffectiveCcEmails({
        recipientEmail:
          String(prev.invoiceId || '').trim() === invoiceId
            ? (prev.recipientEmail || editorForm.clientEmail || '')
            : (editorForm.clientEmail || ''),
        additionalRecipientEmailsText:
          String(prev.invoiceId || '').trim() === invoiceId
            ? prev.additionalRecipientEmailsText
            : '',
        additionalCcEmailsText:
          String(prev.invoiceId || '').trim() === invoiceId
            ? prev.additionalCcEmailsText
            : '',
        clientEmail: editorForm.clientEmail || '',
      }),
      pdfFile: prev.pdfFile,
    }));
    setQuickSendOpen(true);

    try {
      await loadDraft({ invoiceId, targetTab, suppressSuccess: true });
    } catch {
      // Keep the quick-send panel available even if the draft refresh fails.
    }
  };

  const submitInvoice = async (event: React.FormEvent) => {
    event.preventDefault();
    clearFeedback();

    if (!hasInvoiceIdentity(editorForm)) {
      setError(isInspectionInvoice ? 'Inspection invoice can be saved after client and inspection address are entered.' : 'Project number or invoice no. is required.');
      return;
    }

    if (!isInspectionInvoice && totals.usedItems.length === 0) {
      setError('Add at least one invoice line item.');
      return;
    }

    setBusy(mode === 'create' ? 'create' : 'amend');
    const previewWindow = openPreviewWindow(mode === 'create' ? 'Saving invoice and preparing PDF preview...' : 'Saving amendments and preparing PDF preview...');
    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const payload = buildInvoicePayload(editorForm, mode === 'amend');

      const response = mode === 'create'
        ? await azureSqlService.createWorkflowInvoice(payload)
        : await azureSqlService.amendWorkflowInvoice(payload);

      const nextForm = syncSavedInvoiceState(editorForm, response);
      const responseInvoiceId = nextForm.invoiceId;
      const responseInvoiceNumber = nextForm.invoiceNumber;
      const startedNewRevision = Boolean(response?.startedNewRevision || response?.data?.startedNewRevision);
      const responseRevisionNo = Number(response?.revisionNo || response?.data?.revisionNo || 0);
      let draftSaveIssue: string | null = null;

      if (responseInvoiceId) {
        try {
          await renderStoreAndOpenDraftPdf(responseInvoiceId, nextForm, previewWindow);
        } catch (draftErr: any) {
          draftSaveIssue = draftErr?.message || 'Draft PDF could not be stored yet.';
          if (previewWindow && !previewWindow.closed) {
            previewWindow.close();
          }
        }

        await loadDraft({ invoiceId: responseInvoiceId, targetTab: 'sheet', suppressSuccess: true });
        await refreshInvoiceOptions();
        const refreshedCategories = await azureSqlService.getInvoiceWorkflowCategories();
        setCategories(Array.isArray(refreshedCategories) ? refreshedCategories : []);
        if (mode === 'create') {
          setPostSavePrompt({
            invoiceId: responseInvoiceId,
            invoiceNumber: responseInvoiceNumber || nextForm.invoiceNumber || responseInvoiceId,
          });
          setActiveTab('sheet');
        } else {
          setActiveTab('finalise');
        }
      } else if (previewWindow && !previewWindow.closed) {
        previewWindow.close();
      }

      const resolvedInvoiceNumber = responseInvoiceNumber || editorForm.invoiceNumber || responseInvoiceId;
      if (draftSaveIssue) {
        setSuccess(mode === 'create'
          ? `Created invoice ${resolvedInvoiceNumber}. The invoice saved successfully, but the draft PDF could not be stored yet: ${draftSaveIssue}`
          : startedNewRevision && responseRevisionNo > 0
            ? `Started revision ${responseRevisionNo} for invoice ${resolvedInvoiceNumber}. The invoice saved successfully, but the draft PDF could not be stored yet: ${draftSaveIssue}`
            : `Saved amendments to invoice ${resolvedInvoiceNumber}. The invoice saved successfully, but the refreshed draft PDF could not be stored yet: ${draftSaveIssue}`);
      } else {
        setSuccess(mode === 'create'
          ? `Created invoice ${resolvedInvoiceNumber} and stored the draft PDF.`
          : startedNewRevision && responseRevisionNo > 0
            ? `Started revision ${responseRevisionNo} for invoice ${resolvedInvoiceNumber}, refreshed the draft PDF, and opened it for review.`
            : `Saved amendments to invoice ${resolvedInvoiceNumber}, refreshed the draft PDF, and opened it for review.`);
      }
    } catch (err: any) {
      if (previewWindow && !previewWindow.closed) {
        previewWindow.close();
      }
      setError(formatUserFacingError(err, mode === 'create' ? 'Invoice draft could not be created.' : 'Invoice amendments could not be saved.'));
    } finally {
      setBusy(null);
    }
  };

  const submitCategory = async (event: React.FormEvent) => {
    event.preventDefault();
    clearFeedback();
    setBusy('category');
    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      await azureSqlService.upsertInvoiceWorkflowCategory(categoryForm);
      const refreshed = await azureSqlService.getInvoiceWorkflowCategories();
      setCategories(Array.isArray(refreshed) ? refreshed : []);
      const savedCategoryName = categoryForm.categoryName;
      setCategoryForm({ categoryId: '', categoryCode: '', categoryName: '', defaultDescription: '' });
      setSuccess(`Saved invoice item category${savedCategoryName ? ` ${savedCategoryName}` : ''}.`);
    } catch (err: any) {
      setError(formatUserFacingError(err, 'Invoice item category could not be saved.'));
    } finally {
      setBusy(null);
    }
  };

  const loadCategoryIntoForm = (category: Category) => {
    setCategoryForm({
      categoryId: String(category.CategoryId || ''),
      categoryCode: String(category.CategoryCode || ''),
      categoryName: String(category.CategoryName || ''),
      defaultDescription: String(category.DefaultDescription || ''),
    });
    clearFeedback();
    setSuccess(`Loaded category ${category.CategoryName || category.CategoryId} into the editor.`);
  };

  const resetCategoryForm = () => {
    setCategoryForm({ categoryId: '', categoryCode: '', categoryName: '', defaultDescription: '' });
    clearFeedback();
  };

  const setCurrentInvoicePaymentState = async (paid: boolean) => {
    clearFeedback();
    if (!canManageInvoiceMutations) {
      setError('Only finance or admin users can update invoice payment state.');
      return;
    }

    const activeInvoiceId = String(editorForm.invoiceId || '').trim();
    if (!activeInvoiceId) {
      setError(`Load or save an invoice before marking it as ${paid ? 'paid' : 'unpaid'}.`);
      return;
    }

    if (currentPaymentState === (paid ? 'Paid' : 'Not paid')) {
      setSuccess(`Invoice ${editorForm.invoiceNumber || activeInvoiceId} is already marked as ${paid ? 'paid' : 'unpaid'}.`);
      return;
    }

    setBusy('mark-paid');
    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      await azureSqlService.updateInvoice(activeInvoiceId, { paid });
      setEditorForm((prev) => ({
        ...prev,
        paymentStatus: paid ? 'Paid' : 'Unpaid',
      }));
      setLoadedInvoiceStatus(paid ? 'Paid' : 'Sent');
      setSendLockResult((current) => {
        if (!current) return current;
        const nextStatus = paid ? 'Paid' : 'Sent';
        return {
          ...current,
          status: nextStatus,
          data: current.data ? {
            ...current.data,
            status: nextStatus,
            paymentStatus: paid ? 'Paid' : 'Unpaid',
          } : current.data,
        };
      });
      setSuccess(`Marked invoice ${editorForm.invoiceNumber || activeInvoiceId} as ${paid ? 'paid' : 'unpaid'}.`);
      void refreshInvoiceOptions().catch(() => undefined);
    } catch (err: any) {
      setError(formatUserFacingError(err, `Invoice could not be marked as ${paid ? 'paid' : 'unpaid'}.`));
    } finally {
      setBusy(null);
    }
  };

  const openInspectionCalendarInvite = (source?: SendLockResult | null) => {
    const sourceData = source?.data || source || latestSendLockData;
    const recipient =
      String(sourceData?.email?.recipient || '').trim()
      || (sourceData?.email?.recipients || []).find((value) => String(value || '').trim())
      || sendForm.recipientEmail
      || editorForm.clientEmail;
    setInspectionCalendarForm(createInspectionCalendarForm(
      String(recipient || '').trim(),
      editorForm.notes,
    ));
    setInspectionCalendarError(null);
    setInspectionCalendarResult(null);
    setInspectionCalendarOpen(true);
  };

  const submitInspectionCalendarInvite = async () => {
    setInspectionCalendarError(null);
    setInspectionCalendarResult(null);

    if (!isInspectionInvoice) {
      setInspectionCalendarError('Calendar invite is only available for inspection invoices.');
      return;
    }
    if (!sendForm.invoiceId.trim()) {
      setInspectionCalendarError('Save or load the inspection invoice before creating a calendar invite.');
      return;
    }

    const attendeeEmail = String(inspectionCalendarForm.attendeeEmail || '').trim();
    if (!attendeeEmail) {
      setInspectionCalendarError('Invoice client email is required for the invitee.');
      return;
    }

    const startDateTime = combineLocalDateTime(inspectionCalendarForm.date, inspectionCalendarForm.time);
    const startDate = new Date(startDateTime);
    if (!startDateTime || Number.isNaN(startDate.getTime())) {
      setInspectionCalendarError('Pick a valid inspection date and time.');
      return;
    }
    if (startDate.getTime() <= Date.now()) {
      setInspectionCalendarError('Pick a future inspection date and time.');
      return;
    }

    const durationMinutes = Number(inspectionCalendarForm.durationMinutes);
    if (!Number.isFinite(durationMinutes) || durationMinutes <= 0 || durationMinutes > 12 * 60) {
      setInspectionCalendarError('Duration must be between 1 minute and 12 hours.');
      return;
    }

    setBusy('inspection-calendar');
    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const response = await azureSqlService.createInspectionCalendarInvite({
        invoiceId: sendForm.invoiceId,
        inspectionJobId: editorForm.inspectionJobId || latestSendLockData?.inspectionJobId || undefined,
        invoiceNumber: editorForm.invoiceNumber || latestSendLockData?.invoiceNumber || undefined,
        startDateTime,
        durationMinutes,
        attendeeEmail,
        description: inspectionCalendarForm.description,
      });
      const data = response?.data || response;
      setInspectionCalendarResult(data || null);
      setSuccess(`Created inspection calendar invite${data?.event?.webLink ? ' in Outlook' : ''}.`);
    } catch (err: any) {
      setInspectionCalendarError(formatUserFacingError(
        err,
        'Inspection calendar invite could not be created.',
        'Check Microsoft Graph calendar permissions for PMapp, then try again.'
      ));
    } finally {
      setBusy(null);
    }
  };

  const previewSendLockEmail = async () => {
    clearFeedback();

    if (!sendForm.invoiceId.trim()) {
      setError('Save or load an invoice before previewing the email.');
      return;
    }

    setBusy('send-lock-preview');
    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const response = await azureSqlService.previewSendLockWorkflowInvoiceEmail({
        invoiceId: sendForm.invoiceId,
        recipientEmail: sendForm.recipientEmail || undefined,
        additionalRecipientEmails: parseEmailListInput(sendForm.additionalRecipientEmailsText),
        clientEmail: editorForm.clientEmail || undefined,
        ccEmails: sendForm.ccEmails,
        invoiceNumber: editorForm.invoiceNumber || undefined,
        clientName: editorForm.clientName || undefined,
        projectNumber: editorForm.projectNumber || undefined,
        projectAddress: editorForm.projectAddress || undefined,
        dueDate: editorForm.dueDate || undefined,
        amount: totals.grandTotal,
        stripePaymentUrl: visibleStripePaymentUrl || undefined,
        stripeSurchargeEnabled: visibleStripePaymentUrl && isStripeSurchargeAllowed() ? editorForm.stripeSurchargeEnabled : false,
        rfiTicketNumber: editorForm.rfiTicketNumber || undefined,
        senderEmail: sender.email || undefined,
        entity: editorForm.entity || undefined,
      });
      setEmailPreview(response || null);
      setEmailComposerDraft((currentDraft) => (
        response
          ? currentDraft ?? {
              subject: String(response.subject || ''),
              html: String(response.html || ''),
            }
          : null
      ));
      setSuccess('Email preview refreshed.');
    } catch (err: any) {
      setError(formatUserFacingError(err, 'Invoice email preview could not load.'));
    } finally {
      setBusy(null);
    }
  };

  const submitSendLock = async (mode: 'file' | 'email' | 'manual') => {
    clearFeedback();

    if (!sendForm.invoiceId.trim()) {
      setError('Save or load an invoice before filing it.');
      return;
    }
    if (isInspectionInvoice && totals.usedItems.length === 0) {
      setError('Add at least one invoice line item before filing or sending the inspection invoice.');
      return;
    }
    if (isInspectionInvoice && !inspectionFolderReady) {
      setError('Create the inspection SharePoint folder before filing or sending this invoice.');
      return;
    }

    setBusy('send-lock');
    try {
      const returnTab: WorkflowTab = quickSendOpen && activeTab !== 'finalise' ? activeTab : 'finalise';
      const sendEmail = mode === 'email';
      const manualEmailSent = mode === 'manual';
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const { form: persistedForm, locked } = await persistInvoiceForOutput();
      let latestDraftPdf = storedDraftPdf;
      let generatedPdfBase64: string | null = null;
      let generatedPdfFileName: string | undefined;

      if (!sendForm.pdfFile) {
        if (locked) {
          const { pdfService } = await import('@/services/pdfService');
          const currentPdfBlob = await pdfService.generateInvoicePDFBlob(buildInvoicePdfPayload(persistedForm));
          generatedPdfBase64 = await blobToBase64(currentPdfBlob);
          generatedPdfFileName = `${persistedForm.invoiceNumber || sendForm.invoiceId}.pdf`;
        } else {
          latestDraftPdf = await renderStoreAndOpenDraftPdf(sendForm.invoiceId, persistedForm, null);
          await loadDraft({ invoiceId: sendForm.invoiceId, targetTab: returnTab, suppressSuccess: true });
        }
      }

      const payload: Record<string, unknown> = {
        invoiceId: sendForm.invoiceId,
        recipientEmail: sendForm.recipientEmail || undefined,
        additionalRecipientEmails: parseEmailListInput(sendForm.additionalRecipientEmailsText),
        clientEmail: editorForm.clientEmail || undefined,
        ccEmails: sendForm.ccEmails,
        draftFileId: sendForm.pdfFile || generatedPdfBase64
          ? undefined
          : (latestDraftPdf?.draftFileId || storedDraftPdf?.draftFileId || undefined),
        sendEmail,
        manualEmailSent,
      };

      if (emailComposerDraft) {
        payload.customEmailSubject = emailComposerDraft.subject;
        payload.customEmailHtml = emailComposerDraft.html;
      }

      if (sendForm.pdfFile) {
        payload.pdfFileName = sendForm.pdfFile.name;
        payload.pdfContentType = sendForm.pdfFile.type || 'application/pdf';
        payload.pdfBase64 = await fileToBase64(sendForm.pdfFile);
      } else if (generatedPdfBase64) {
        payload.pdfFileName = generatedPdfFileName;
        payload.pdfContentType = 'application/pdf';
        payload.pdfBase64 = generatedPdfBase64;
      }

      const response = await azureSqlService.sendLockWorkflowInvoice(payload);
      const result = response?.data || response;
      if (response?.success === false || result?.sharePointUpload?.integrationStatus !== 'configured') {
        throw new Error(response?.error?.message || 'PMapp did not confirm that the invoice was filed. Check the invoice before trying again.');
      }
      setSendLockResult(response || null);
      await refreshInvoiceOptions();
      setPostSavePrompt(null);
      const email = response?.email || response?.data?.email;
      const savedStatus = result.status || 'Filed';
      setLoadedInvoiceStatus(savedStatus);
      setLoadedRevisionNo(Number(response?.revisionNo || response?.data?.revisionNo || currentRevisionNo || 1));
      setLoadedHasPendingRevision(false);
      if (response?.supersededRevisionNo || response?.data?.supersededRevisionNo) {
        setLoadedLatestIssuedRevisionNo(Number(response?.revisionNo || response?.data?.revisionNo || currentRevisionNo || 1));
        setLoadedLatestIssuedRevisionStatus(savedStatus);
      }
      if ((sendEmail || manualEmailSent) && email?.sent !== true) {
        setSuccess(null);
        setError(`Invoice ${result.invoiceNumber || editorForm.invoiceNumber} was filed, but sending was not confirmed. ${email?.error || 'Check Sent Items before trying again.'}`);
      } else if (result.persistenceWarning) {
        setSuccess(null);
        setError(result.persistenceWarning);
      } else {
        const invoiceLabel = response?.invoiceNumber || response?.data?.invoiceNumber || editorForm.invoiceNumber;
        const storedDraftSuffix = locked && !sendForm.pdfFile
          ? ' using a freshly rendered current PDF'
          : '';
        setSuccess(
          sendEmail
            ? `Filed and emailed invoice ${invoiceLabel}${storedDraftSuffix}.`
            : manualEmailSent
              ? `Filed invoice ${invoiceLabel}${storedDraftSuffix} and marked it as emailed manually.`
              : `Filed invoice ${invoiceLabel} to SharePoint${storedDraftSuffix}.`
        );
      }
      if (isInspectionInvoice && (sendEmail || manualEmailSent) && email?.sent === true && !result.persistenceWarning) {
        openInspectionCalendarInvite(response || null);
      }
    } catch (err: any) {
      setError(formatUserFacingError(
        err,
        mode === 'email'
          ? 'Invoice could not be filed and emailed.'
          : mode === 'manual'
            ? 'Invoice could not be filed and marked as emailed.'
            : 'Invoice could not be filed.'
      ));
    } finally {
      setBusy(null);
    }
  };

  const resetEmailComposerDraft = () => {
    setEmailComposerDraft(emailPreview ? {
      subject: String(emailPreview.subject || ''),
      html: String(emailPreview.html || ''),
    } : null);
  };

  const renderEmailPreviewComposer = (maxHeight: number) => {
    const previewToEmails = sendRecipientEmails.length > 0
      ? sendRecipientEmails
      : emailPreview?.recipientEmails || [];
    const previewCcEmails = sendForm.ccEmails.length > 0
      ? sendForm.ccEmails
      : [];

    return (
    <div className="wdgf-notice wdgf-notice--warning" style={{ fontSize: 13, lineHeight: 1.7 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
        <div>
          <div style={{ fontWeight: 800 }}>Invoice email preview</div>
          <div style={{ fontSize: 12, color: '#34579e' }}>
            Preview the email PMapp will use, then edit the subject or body inline before you click <strong>Confirm, file, and email</strong>.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={resetEmailComposerDraft}
            disabled={!emailPreview}
            className="wdgf-button"
            style={{ color: '#234ea8', borderColor: 'rgba(47,111,212,0.24)' }}
          >
            Reset edits
          </button>
          <button
            type="button"
            onClick={previewSendLockEmail}
            disabled={busy === 'send-lock-preview' || !sendForm.invoiceId.trim()}
            className="wdgf-button"
            style={{ color: '#234ea8', borderColor: 'rgba(47,111,212,0.42)' }}
          >
            {busy === 'send-lock-preview' ? 'Loading preview...' : 'Preview email'}
          </button>
        </div>
      </div>
      <div><strong>To:</strong> {previewToEmails.length > 0 ? previewToEmails.join(', ') : 'Not set yet'}</div>
      <div><strong>CC:</strong> {previewCcEmails.length > 0 ? previewCcEmails.join(', ') : 'None'}</div>
      <div><strong>From:</strong> {emailPreview?.senderEmail || sender.email || 'Not available yet'}</div>
      <div style={{ marginTop: 10, fontSize: 12, color: '#34579e' }}>
        Recipient changes update the To and CC lists without replacing the edited subject or body. Use reset to restore the latest preview defaults.
      </div>
      <div
        style={{
          marginTop: 10,
          borderRadius: 10,
          border: '1px solid rgba(144,163,195,0.24)',
          background: '#fff',
          padding: 14,
          maxHeight,
          overflowY: 'auto',
          color: '#1f2937',
        }}
      >
        {emailPreview ? (
          <div style={{ display: 'grid', gap: 10 }}>
            <label style={{ display: 'grid', gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', color: '#34579e', textTransform: 'uppercase' }}>Subject</span>
              <input
                style={inputBase}
                value={emailComposerDraft?.subject ?? ''}
                onChange={(event) => setEmailComposerDraft((prev) => ({
                  subject: event.target.value,
                  html: prev?.html ?? String(emailPreview.html || ''),
                }))}
                placeholder="Email subject"
              />
            </label>
            <div style={{ display: 'grid', gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', color: '#34579e', textTransform: 'uppercase' }}>Email body</span>
              <div style={{ borderRadius: 10, border: '1px solid rgba(144,163,195,0.24)', background: '#fff', padding: 12 }}>
                <EditableEmailHtml
                  value={emailComposerDraft?.html ?? ''}
                  onChange={(nextHtml) => setEmailComposerDraft((prev) => ({
                    subject: prev?.subject ?? String(emailPreview.subject || ''),
                    html: nextHtml,
                  }))}
                  minHeight={180}
                />
              </div>
            </div>
            <div style={{ fontSize: 12, color: '#34579e' }}>
              PMapp will send the edited subject and body exactly as shown here when you confirm and email.
            </div>
          </div>
        ) : (
          <div style={{ color: '#64748b' }}>
            No email preview loaded yet. Use <strong>Preview email</strong> to load the current email, then edit it inline before sending.
          </div>
        )}
      </div>
    </div>
  );
  };

  const visibleRows = Array.from({ length: Math.max(MIN_VISIBLE_ROWS, editorForm.items.length) }, (_, index) => editorForm.items[index] || null);

  return (
    <div className="wdgf-page">
      <div className="wdgf-page__hero">
        <div className="wdgf-page__hero-inner">
          <div className="wdgf-page__hero-row">
            <div className="wdgf-page__hero-title">
              <button
                type="button"
                onClick={() => navigate('/invoices')}
                className="wdgf-page__back-button"
              >
                Back
              </button>
              <div>
                <div className="wdgf-page__hero-kicker">Invoice</div>
                <h1>Create, amend, finalise</h1>
              </div>
            </div>

            <div className="wdgf-mode-switch">
            <button
              type="button"
              onClick={resetForCreate}
              className={`wdgf-page__mode-button${mode === 'create' && !isInspectionInvoice ? ' wdgf-page__mode-button--active' : ''}`}
              style={{
                fontWeight: 700,
              }}
            >
              Create
            </button>
            <button
              type="button"
              onClick={() => {
                setMode('amend');
                setActiveTab('amend');
                clearFeedback();
              }}
              className={`wdgf-page__mode-button${mode === 'amend' ? ' wdgf-page__mode-button--active' : ''}`}
              style={{
                fontWeight: 700,
              }}
            >
              Amend
            </button>
          </div>
        </div>
        <div className="wdgf-page__hero-copy">
          Find or enter the job, fill the invoice, then save or finalise without leaving the workflow.
        </div>
      </div>
      </div>

      <div className="wdgf-page__content">
        <div style={{ display: 'grid', gap: 12 }}>
          <div className="wdgf-summary-grid wdgf-summary-grid--invoice">
            <div className="wdgf-summary-card">
              <div className="wdgf-summary-card__label">Current invoice</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>{editorForm.invoiceNumber || 'New invoice draft'}</div>
              <div style={{ marginTop: 8 }}>
                <StatusBadge status={currentInvoiceStatus} size="sm">
                  {currentInvoiceStatus}
                </StatusBadge>
              </div>
              <div style={{ marginTop: 8, color: '#475569', fontSize: 13 }}>{currentRevisionCaption}</div>
              <div style={{ marginTop: 4, color: '#475569', fontSize: 13 }}>
                {isInspectionInvoice
                  ? (inspectionFolderReady ? `Folder ready: ${inspectionFolderPath}` : 'Inspection folder not created yet')
                  : (editorForm.projectNumber || 'Project number pending')}
              </div>
              {editorForm.rfiTicketNumber ? (
                <div style={{ marginTop: 4, color: '#475569', fontSize: 13 }}>RFI {editorForm.rfiTicketNumber}</div>
              ) : null}
              {editorForm.taskId ? (
                <div style={{ marginTop: 4, color: '#475569', fontSize: 13 }}>Linked task #{editorForm.taskId}</div>
              ) : null}
            </div>
            <div className="wdgf-summary-card">
              <div className="wdgf-summary-card__label">Invoice source</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>
                {isInspectionInvoice ? 'Inspection invoice' : invoiceSource === 'civil' ? 'Civil invoice source' : invoiceSource === 'current' ? 'Current invoice source' : 'Will resolve from your account'}
              </div>
              <div style={{ marginTop: 4, color: '#475569', fontSize: 13 }}>{invoiceSourceCaption}</div>
            </div>
            <div className="wdgf-summary-card">
              <div className="wdgf-summary-card__label">Stored draft PDF</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{storedDraftPdf?.fileName || 'Not linked yet'}</div>
              <div style={{ marginTop: 4, color: '#475569', fontSize: 13 }}>
                {storedDraftPdf?.createdDate ? `Updated ${formatDraftTimestamp(storedDraftPdf.createdDate)}` : 'Render or save the draft to link a PDF here'}
              </div>
            </div>
          </div>

          <div className="wdgf-mobile-summary-strip" aria-label="Invoice status summary">
            <div className="wdgf-mobile-summary-head">
              <div className="wdgf-mobile-summary-label">Invoice</div>
              <div className="wdgf-mobile-summary-title">
                <span>{editorForm.invoiceNumber || 'New invoice draft'}</span>
                <StatusBadge status={currentInvoiceStatus} size="sm">
                  {currentInvoiceStatus}
                </StatusBadge>
              </div>
            </div>
            <div className="wdgf-mobile-summary-meta">
              <span>{currentRevisionCaption}</span>
              <span>
                {isInspectionInvoice
                  ? inspectionFolderReady ? 'Folder ready' : 'Folder pending'
                  : editorForm.projectNumber || 'Project pending'}
              </span>
              <span>
                Source {isInspectionInvoice ? 'inspection' : invoiceSource === 'civil' ? 'civil' : invoiceSource === 'current' ? 'current' : 'auto'}
              </span>
              <span>{storedDraftPdf?.fileName ? 'PDF linked' : 'PDF not linked'}</span>
            </div>
          </div>

          <div className="wdgf-tabs">
            {[
              { key: 'sheet' as const, label: 'Invoice sheet', shortLabel: 'Sheet' },
              { key: 'reset' as const, label: 'New Invoice', shortLabel: 'New' },
              { key: 'inspection' as const, label: 'Inspection', shortLabel: 'INX' },
              { key: 'amend' as const, label: 'Amend existing', shortLabel: 'Amend' },
              { key: 'finalise' as const, label: 'Finalise and send', shortLabel: 'Send' },
              { key: 'categories' as const, label: 'Reusable categories', shortLabel: 'Items' },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => {
                  if (tab.key === 'reset') {
                    resetForCreate();
                    return;
                  }
                  if (tab.key === 'inspection') {
                    startInspectionInvoice();
                    return;
                  }
                  setActiveTab(tab.key);
                }}
                className={`wdgf-page__tab${tab.key === 'inspection' ? (isInspectionInvoice ? ' wdgf-page__tab--active' : '') : (tab.key !== 'reset' && activeTab === tab.key ? ' wdgf-page__tab--active' : '')}`}
              style={{ fontWeight: 700 }}
              >
                <span className="wdgf-tab-label-full">{tab.label}</span>
                <span className="wdgf-tab-label-short">{tab.shortLabel}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="wdgf-panel" style={{ display: activeTab === 'sheet' || activeTab === 'amend' ? 'block' : 'none' }}>
          {!(isPhone && activeTab === 'sheet') ? (
          <div className="wdgf-panel__subheader">
            {!isPhone ? (
            <div style={{ position: 'relative' }}>
              <label className="wdgf-section-label">
                {isInspectionInvoice
                  ? 'Inspection address'
                  : activeTab === 'sheet'
                  ? (mode === 'create' ? 'Project number / project lookup' : 'Switch to create mode for project lookup')
                  : 'Project lookup stays on the invoice sheet tab'}
              </label>
              <input
                style={inputBase}
                placeholder={isInspectionInvoice ? 'Inspection address' : 'Type project number, address, or name'}
                value={isInspectionInvoice ? editorForm.projectAddress : projectQuery}
                onChange={(event) => {
                  if (isInspectionInvoice) {
                    updateForm('projectAddress', event.target.value);
                    return;
                  }
                  handleProjectQueryChange(event.target.value);
                }}
                onBlur={() => {
                  if (!isInspectionInvoice) {
                    commitProjectLookup(projectQuery);
                  }
                }}
                disabled={activeTab !== 'sheet' || (mode !== 'create' && !isInspectionInvoice)}
              />
              {activeTab === 'sheet' && mode === 'create' && !isInspectionInvoice && projectOptions.length > 0 ? (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, marginTop: 6, background: '#fff', border: '1px solid rgba(144,163,195,0.22)', borderRadius: 14, boxShadow: '0 18px 40px rgba(15,23,42,0.12)', overflow: 'hidden' }}>
                  {projectOptions.map((project) => (
                    <button
                      key={project.ProjectId}
                      type="button"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        selectProject(project);
                      }}
                      style={{ width: '100%', border: 'none', background: '#fff', padding: '12px 14px', textAlign: 'left', cursor: 'pointer' }}
                    >
                      <div style={{ fontWeight: 700, color: '#111827' }}>{project.ProjectCode || `Project ${project.ProjectId}`}</div>
                      <div style={{ fontSize: 12, color: '#475569' }}>
                        {formatProjectAddressParts(project.SiteAddress, project.Suburb, project.PostCode) || project.ProjectName || 'No project address'}
                      </div>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            ) : null}

            <div style={{ position: 'relative', display: activeTab === 'amend' ? 'block' : 'none' }}>
              <label className="wdgf-section-label">Amend existing invoice</label>
              <div className="wdgf-inline-control">
                <input
                  style={inputBase}
                  placeholder="Type invoice number"
                  value={invoiceLookup}
                  onChange={(event) => setInvoiceLookup(event.target.value)}
                />
                <button
                  type="button"
                  onClick={() => loadDraft({ invoiceNumber: invoiceLookup })}
                  disabled={busy === 'load-draft'}
                  className="wdgf-button--primary"
                  style={{ paddingInline: 14 }}
                >
                  {busy === 'load-draft' ? 'Loading...' : 'Load'}
                </button>
              </div>
              {activeTab === 'amend' ? (
                <ActionFeedbackLine tone={feedbackTone} message={feedbackMessage} />
              ) : null}
              {filteredInvoiceOptions.length > 0 ? (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, marginTop: 6, background: '#fff', border: '1px solid rgba(148,163,184,0.22)', borderRadius: 14, boxShadow: '0 18px 40px rgba(15,23,42,0.12)', overflow: 'hidden' }}>
                  {filteredInvoiceOptions.map((invoice) => (
                    <button
                      key={String(invoice.InvoiceId || invoice.InvoiceNumber)}
                      type="button"
                      onClick={() => {
                        setInvoiceLookup(invoice.InvoiceNumber || '');
                        loadDraft({ invoiceId: invoice.InvoiceId ? String(invoice.InvoiceId) : undefined, invoiceNumber: invoice.InvoiceNumber });
                      }}
                      style={{ width: '100%', border: 'none', background: '#fff', padding: '12px 14px', textAlign: 'left', cursor: 'pointer' }}
                    >
                      <div style={{ fontWeight: 700, color: '#111827' }}>{invoice.InvoiceNumber || `Invoice ${invoice.InvoiceId}`}</div>
                      <div style={{ fontSize: 12, color: '#475569' }}>{invoice.Client || invoice.Description || 'No client details'}</div>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            {!isPhone ? (
            <div>
              <label className="wdgf-section-label">Invoice source</label>
              <div style={{ ...inputBase, minHeight: 42, display: 'flex', alignItems: 'center', fontWeight: 700 }}>
                {isInspectionInvoice ? 'Inspection / INX source' : invoiceSource === 'civil' ? 'Civil invoice source' : invoiceSource === 'current' ? 'Current invoice source' : 'Will resolve from your account'}
              </div>
            </div>
            ) : null}
          </div>
          ) : null}
          {activeTab === 'sheet' && feedbackMessage && !isPhone ? (
            <div className="wdgf-panel__inline-feedback">
              <ActionFeedbackLine tone={feedbackTone} message={feedbackMessage} />
            </div>
          ) : null}

          {activeTab === 'amend' ? (
            <div style={{ padding: 20, background: '#fff' }}>
              <div className="wdgf-subpanel">
                <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>Amend existing invoice</div>
                <div>Load an invoice here, then continue editing on the invoice sheet tab.</div>
                <div>Current loaded invoice: <strong>{editorForm.invoiceNumber || 'None loaded yet'}</strong></div>
                <div>Current client: <strong>{editorForm.clientName || 'Not loaded yet'}</strong></div>
              </div>
            </div>
          ) : null}

          <form onSubmit={submitInvoice} style={{ display: activeTab === 'sheet' ? 'block' : 'none' }}>
            <div style={{ padding: 20, background: 'linear-gradient(180deg, #ffffff 0%, #fbfbfc 100%)' }}>
              {(!isPhone || mode === 'amend') ? (
              <div className="wdgf-subpanel wdgf-subpanel--highlight wdgf-sheet-notes-panel" style={{ marginBottom: isPhone ? 8 : 14 }}>
                <label className="wdgf-sheet-notes-panel__label">
                  Notes
                </label>
                <textarea
                  className="wdgf-sheet-notes-panel__textarea"
                  style={{ ...inputBase, minHeight: isPhone ? 52 : 92, background: '#fff' }}
                  value={editorForm.notes}
                  onChange={(event) => updateForm('notes', event.target.value)}
                  placeholder="Internal notes before previewing, filing, or sending."
                />
              </div>
              ) : null}

              {isInspectionInvoice ? (
                <div className="wdgf-subpanel" style={{ marginBottom: 14, display: 'grid', gap: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#34579e' }}>
                        Inspection job
                      </div>
                      <div style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>
                        {editorForm.invoiceNumber || 'INX number will be assigned on save'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={useClientAddressForInspection}
                        className="wdgf-button"
                      >
                        Use client address
                      </button>
                      <button
                        type="button"
                        onClick={createInspectionFolder}
                        disabled={busy === 'inspection-folder' || !editorForm.invoiceId}
                        className="wdgf-button--primary"
                      >
                        {busy === 'inspection-folder' ? 'Creating...' : inspectionFolderReady ? 'Refresh folder' : 'Create folder'}
                      </button>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isPhone ? '1fr' : '1fr minmax(220px, 0.5fr)', gap: 10 }}>
                    <textarea
                      style={{ ...inputBase, minHeight: 76, resize: 'vertical' }}
                      value={editorForm.projectAddress}
                      onChange={(event) => updateForm('projectAddress', event.target.value)}
                      placeholder="Inspection address"
                    />
                    <div style={{ ...inputBase, minHeight: 76, display: 'grid', alignContent: 'center', gap: 4, background: '#f8fafc' }}>
                      <div style={{ fontWeight: 800, color: inspectionFolderReady ? '#166534' : '#92400e' }}>
                        {inspectionFolderReady ? 'Folder ready' : 'Folder pending'}
                      </div>
                      {inspectionFolderUrl ? (
                        <a href={inspectionFolderUrl} target="_blank" rel="noreferrer" style={{ color: '#234ea8', fontWeight: 700 }}>
                          Open inspection folder
                        </a>
                      ) : (
                        <span style={{ color: '#64748b' }}>{inspectionFolderPath || 'Save draft first'}</span>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}

              {(!isPhone || Boolean(editorForm.invoiceId) || currentPaymentState === 'Paid') ? (
              <div
                className="wdgf-subpanel wdgf-payment-state-panel"
                style={{
                  marginBottom: isPhone ? 8 : 14,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <div className="wdgf-payment-state-panel__body">
                  <div className="wdgf-payment-state-panel__heading">
                    <span>Payment</span>
                    <StatusBadge status={currentPaymentState === 'Paid' ? 'Paid' : 'Pending'} size="sm">
                      {currentPaymentState}
                    </StatusBadge>
                  </div>
                  <span className="wdgf-payment-state-panel__help">
                    {!canManageInvoiceMutations
                      ? 'Only finance/admin users can update payment here.'
                      : editorForm.invoiceId
                        ? 'Shortcut for invoices paid or corrected outside PMapp.'
                        : 'Save invoice first to enable this action.'}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => void setCurrentInvoicePaymentState(currentPaymentState !== 'Paid')}
                  disabled={!canManageInvoiceMutations || !editorForm.invoiceId || busy !== null}
                  className={currentPaymentState === 'Paid' ? 'wdgf-button' : 'wdgf-button--primary'}
                >
                  {busy === 'mark-paid' ? 'Saving...' : currentPaymentState === 'Paid' ? 'Mark unpaid' : 'Mark paid'}
                </button>
              </div>
              ) : null}

              {canManageStripePaymentLinks && canUseStripeForEntity ? (
                <div className="wdgf-subpanel" style={{ marginBottom: 14, display: 'grid', gap: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#34579e' }}>
                        Online payment
                      </div>
                      <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, marginTop: 4 }}>
                        YL can choose whether this invoice stays bank-transfer only or gets a Stripe Payment Link tied back to the invoice number.
                      </div>
                    </div>
                    {visibleStripePaymentUrl ? (
                      <a href={visibleStripePaymentUrl} target="_blank" rel="noreferrer" style={{ color: '#234ea8', fontWeight: 700, fontSize: 13 }}>
                        Open current payment page
                      </a>
                    ) : null}
                  </div>
                  <div style={{ display: 'grid', gap: 8, gridTemplateColumns: isPhone ? '1fr' : 'minmax(240px, 420px) 1fr' }}>
                    <div style={{ display: 'grid', gap: 8 }}>
                      <select
                        style={inputBase}
                        value={editorForm.stripePaymentMode}
                        onChange={(event) => updateForm('stripePaymentMode', event.target.value)}
                      >
                        <option value="none">Bank transfer only</option>
                        <option value="payment_link">Stripe payment link</option>
                        {editorForm.stripePaymentMode === 'checkout_session' ? (
                          <option value="checkout_session">Legacy Stripe checkout link</option>
                        ) : null}
                      </select>
                      {editorForm.stripePaymentMode === 'payment_link' && stripeSurchargeAllowed ? (
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '10px 12px',
                            borderRadius: 12,
                            border: '1px solid rgba(144, 163, 195, 0.28)',
                            background: '#f8fafc',
                            color: '#1e293b',
                            fontSize: 13,
                            fontWeight: 600,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={editorForm.stripeSurchargeEnabled}
                            onChange={(event) => updateStripeSurchargeEnabled(event.target.checked)}
                          />
                          <span>Add 1.7% surcharge</span>
                        </label>
                      ) : null}
                    </div>
                    <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
                      {editorForm.stripePaymentMode === 'payment_link'
                        ? (!stripeSurchargeAllowed
                          ? `Saving creates a new one-off Stripe Payment Link for this invoice with no Stripe surcharge. Card surcharges are disabled after ${STRIPE_SURCHARGE_EXPIRY_DISPLAY_DATE}.`
                          : editorForm.stripeSurchargeEnabled
                          ? `Saving creates a new one-off Stripe Payment Link for this invoice, adds a flat 1.7% Stripe Surcharge on the hosted payment page, expires after ${STRIPE_SURCHARGE_EXPIRY_DISPLAY_DATE}, and collects contact name, email, and business name for reconciliation.`
                          : 'Saving creates a new one-off Stripe Payment Link for this invoice, does not add a Stripe surcharge on the hosted payment page, and collects contact name, email, and business name for reconciliation.')
                        : editorForm.stripePaymentMode === 'checkout_session'
                          ? 'This invoice currently has a legacy Stripe checkout link. Save again with Stripe payment link selected to replace it with the new Payment Links flow.'
                          : 'Saving clears any existing Stripe payment URL from this invoice and leaves the payment instructions as bank transfer / PayID only.'}
                    </div>
                  </div>
                </div>
              ) : null}

              {isPhone ? (
                <InvoiceWorkflowPhoneSheet
                  editorForm={editorForm}
                  items={editorForm.items}
                  categories={categories}
                  selectableClients={selectableClients}
                  selectedClientId={selectedClientId}
                  clientSearchQuery={clientSearchQuery}
                  filteredClients={filteredSelectableClients}
                  clientSearchStatus={clientSearchStatus}
                  hasClientSearchQuery={hasClientSearchQuery}
                  sender={sender}
                  totals={totals}
                  mode={mode}
                  busy={busy}
                  feedbackTone={feedbackTone}
                  feedbackMessage={feedbackMessage || (postSavePrompt ? `Saved ${postSavePrompt.invoiceNumber}. Finalise now or keep editing.` : null)}
                  onUpdateForm={updateForm}
                  onToggleStripeSurcharge={updateStripeSurchargeEnabled}
                  stripeSurchargeAllowed={stripeSurchargeAllowed}
                  onUpdateItem={updateItem}
                  onProjectQueryChange={(value) => {
                    if (isInspectionInvoice) {
                      updateForm('projectAddress', value);
                      return;
                    }
                    handleProjectQueryChange(value);
                  }}
                  onProjectBlur={() => {
                    if (mode === 'create' && !isInspectionInvoice) {
                      commitProjectLookup(editorForm.projectNumber);
                    }
                  }}
                  onSelectClient={selectClientFromDirectory}
                  onClientSearchQueryChange={setClientSearchQuery}
                  onApplyClient={applySelectedClient}
                  onAddItem={addItemRow}
                  onRemoveItem={removeItemRow}
                  onPreview={previewRenderedDraftPdf}
                  onFinalise={() => { void openQuickFinalisePanel('finalise'); }}
                  showStripePaymentLinkOption={canManageStripePaymentLinks && canUseStripeForEntity}
                />
              ) : null}

              {!isPhone ? (
              <div className="wdgf-sheet-shell">
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 1.1fr) minmax(280px, 1fr)', borderBottom: '1px solid rgba(144, 163, 195, 0.22)' }}>
                  <div style={{ padding: '18px 18px 16px', minHeight: 128, display: 'flex', alignItems: 'center', borderRight: '1px solid rgba(144, 163, 195, 0.22)', background: 'linear-gradient(180deg, rgba(246,249,253,0.98) 0%, rgba(255,255,255,0.98) 100%)' }}>
                    <EntityLogo entity={editorForm.entity} size="horizontal" width={280} height={90} style={{ maxWidth: '100%', height: 'auto' }} />
                  </div>
                  <div>
                    <div style={{ padding: '14px 18px 6px', fontSize: 20, fontWeight: 800, letterSpacing: '0.08em', color: '#10243f' }}>INVOICE</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', borderTop: '1px solid rgba(144, 163, 195, 0.22)' }}>
                      {[
                        { label: 'Invoice no.', field: 'invoiceNumber' as const },
                        { label: 'Date', field: 'invoiceDate' as const },
                        { label: 'Due Date', field: 'dueDate' as const },
                      ].map((column) => (
                        <div key={column.field} style={{ padding: '12px 16px 14px', borderLeft: column.field === 'invoiceNumber' ? 'none' : '1px solid rgba(144, 163, 195, 0.18)' }}>
                          <div style={{ fontSize: 13, marginBottom: 8, color: '#5d6d84' }}>{column.label}</div>
                          <input
                            style={{ ...sheetInput, fontSize: 18, fontWeight: 800 }}
                            value={editorForm[column.field]}
                            onChange={(event) => updateForm(column.field, event.target.value)}
                            type={column.field.includes('Date') ? 'date' : 'text'}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 0.82fr) minmax(240px, 0.72fr) minmax(440px, 1.46fr)', borderBottom: '1px solid rgba(144, 163, 195, 0.22)' }}>
                  <div style={{ padding: '22px 18px', borderRight: '1px solid rgba(144, 163, 195, 0.22)', background: 'linear-gradient(180deg, rgba(234,241,255,0.92) 0%, rgba(255,255,255,0.98) 100%)' }}>
                    <div style={{ fontSize: 14, letterSpacing: '0.08em', marginBottom: 12, color: '#34579e' }}>TOTAL DUE</div>
                    <div style={{ fontSize: 40, fontWeight: 800, color: '#10243f' }}>{formatMoney(totals.grandTotal)}</div>
                  </div>

                  <div style={{ padding: '18px', borderRight: '1px solid rgba(144, 163, 195, 0.22)', background: 'linear-gradient(180deg, rgba(246,249,253,0.98) 0%, rgba(255,255,255,0.98) 100%)' }}>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 800, color: '#34579e', marginBottom: 6 }}>Entity</label>
                    <select style={inputBase} value={editorForm.entity} onChange={(event) => updateForm('entity', event.target.value)}>
                      <option value="Linfinity Engineering Pty Ltd">Linfinity Engineering Pty Ltd</option>
                      <option value="Equilibrium Engineering Pty Ltd">Equilibrium Engineering Pty Ltd</option>
                    </select>
                    <div style={{ marginTop: 12, fontSize: 12, color: '#475569', lineHeight: 1.6 }}>
                      <div>{sender.displayName}</div>
                      <div>{sender.address}</div>
                    </div>
                  </div>

                  <div className="wdgf-dark-sheet" style={{ padding: '16px 24px' }}>
                    <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Invoice to</div>
                    <input
                      style={{ ...inputBase, marginBottom: 8, background: 'rgba(255,255,255,0.14)', color: '#fff', border: '1px solid rgba(255,255,255,0.24)' }}
                      value={clientSearchQuery}
                      onChange={(event) => setClientSearchQuery(event.target.value)}
                      placeholder="Search saved clients"
                    />
                    {hasClientSearchQuery ? (
                      <div style={{ marginBottom: 10, display: 'grid', gap: 6 }}>
                        <div style={{ maxHeight: 170, overflowY: 'auto', display: 'grid', gap: 6 }}>
                          {filteredSelectableClients.map((client) => {
                            const clientId = String(client.ClientId || '');
                            const isSelected = clientId !== '' && clientId === selectedClientId;
                            const secondaryText = [client.Company, client.Email || client.Phone, client.Address].filter(Boolean).join(' · ');
                            return (
                              <button
                                key={String(client.ClientId || `${client.Name}-${client.Email}-${client.Phone}`)}
                                type="button"
                                onClick={() => applySelectedClient(client)}
                                style={{
                                  borderRadius: 12,
                                  border: isSelected ? '1px solid rgba(255,255,255,0.72)' : '1px solid rgba(255,255,255,0.16)',
                                  background: isSelected ? 'rgba(255,255,255,0.22)' : 'rgba(15,23,42,0.16)',
                                  color: '#fff',
                                  textAlign: 'left',
                                  padding: '10px 12px',
                                  cursor: 'pointer',
                                }}
                              >
                                <div style={{ fontSize: 14, fontWeight: 700 }}>{client.Name}</div>
                                {secondaryText ? (
                                  <div style={{ marginTop: 4, fontSize: 12, color: 'rgba(255,255,255,0.78)' }}>{secondaryText}</div>
                                ) : null}
                              </button>
                            );
                          })}
                        </div>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.72)' }}>{clientSearchStatus}</div>
                      </div>
                    ) : null}
                    <input style={{ ...sheetInput, color: '#fff', fontSize: 18, marginBottom: 8 }} value={editorForm.clientName} onChange={(event) => updateForm('clientName', event.target.value)} placeholder="Client name" />
                    <input style={{ ...sheetInput, color: '#fff', fontSize: 15, marginBottom: 8 }} value={editorForm.clientCompany} onChange={(event) => updateForm('clientCompany', event.target.value)} placeholder="Company" />
                    <textarea
                      style={{ ...sheetInput, color: '#fff', fontSize: 15, minHeight: 68, resize: 'vertical', lineHeight: 1.45 }}
                      value={editorForm.clientAddress}
                      onChange={(event) => updateForm('clientAddress', event.target.value)}
                      placeholder="Client address"
                    />
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(90px, 0.42fr) minmax(240px, 1.58fr)', gap: 14, marginTop: 12 }}>
                      <input
                        style={{ ...sheetInput, color: '#fff', fontWeight: 500, minWidth: 0 }}
                        type="tel"
                        value={editorForm.clientMobile}
                        onChange={(event) => updateForm('clientMobile', event.target.value)}
                        onBlur={() => formatEditorPhoneField('clientMobile')}
                        placeholder="M:"
                      />
                      <input
                        style={{ ...sheetInput, color: '#fff', fontWeight: 500, minWidth: 0 }}
                        value={editorForm.clientEmail}
                        onChange={(event) => updateForm('clientEmail', event.target.value)}
                        placeholder="E:"
                      />
                    </div>
                  </div>
                </div>

                <div style={{ padding: 14 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '130px 250px minmax(210px, 1fr) 120px 90px 130px 90px', border: '1px solid rgba(144, 163, 195, 0.22)' }}>
                    {(isInspectionInvoice ? ['Job Number', 'Inspection address', 'Item', 'RATE', 'Qty', 'TOTAL', 'Excl. GST'] : ['Project', 'Address', 'Item', 'RATE', 'Qty', 'TOTAL', 'Excl. GST']).map((title, index) => (
                      <div
                        key={title}
                        style={{
                          padding: '12px 10px',
                          fontWeight: 800,
                          textAlign: index >= 3 ? 'center' : index === 2 ? 'left' : 'center',
                          background: index <= 2 ? '#edf3fb' : '#34527f',
                          color: index <= 2 ? '#10243f' : '#fff',
                          borderRight: index === 6 ? 'none' : '1px solid rgba(255,255,255,0.35)',
                        }}
                      >
                        {title}
                      </div>
                    ))}

                    {visibleRows.map((row, index) => {
                      const leftBackground = index % 2 === 0 ? '#f7faff' : '#eef3f9';
                      const rightBackground = index % 2 === 0 ? '#e6eef9' : '#edf3f9';
                      const amount = row ? Number(row.quantity || 0) * Number(row.unitPrice || 0) : 0;

                      return (
                        <React.Fragment key={`row-${index}`}>
                          <div style={{ padding: '10px 8px', background: leftBackground, borderTop: '1px solid #fff', borderRight: '1px solid #fff', display: 'flex', alignItems: index === 0 ? 'flex-start' : 'center' }}>
                            {index === 0 ? (
                              <input
                                style={{ ...sheetInput, textAlign: 'center', fontWeight: 800 }}
                                value={isInspectionInvoice ? editorForm.invoiceNumber : editorForm.projectNumber}
                                onChange={(event) => {
                                  if (!isInspectionInvoice) {
                                    handleProjectQueryChange(event.target.value);
                                  }
                                }}
                                onBlur={() => {
                                  if (mode === 'create' && !isInspectionInvoice) {
                                    commitProjectLookup(editorForm.projectNumber);
                                  }
                                }}
                                placeholder={isInspectionInvoice ? 'Job number pending' : 'Project'}
                                readOnly={isInspectionInvoice}
                              />
                            ) : null}
                          </div>

                          <div style={{ padding: '10px 10px', background: leftBackground, borderTop: '1px solid #fff', borderRight: '1px solid #fff' }}>
                            {index === 0 ? (
                              <textarea
                                style={{ ...sheetInput, minHeight: 42, resize: 'vertical', fontWeight: 500 }}
                                value={editorForm.projectAddress}
                                onChange={(event) => updateForm('projectAddress', event.target.value)}
                                placeholder={isInspectionInvoice ? 'Inspection address' : 'Project address'}
                              />
                            ) : null}
                          </div>

                          <div style={{ padding: '8px 10px', background: leftBackground, borderTop: '1px solid #fff', borderRight: '1px solid #fff' }}>
                            {row ? (
                              <>
                                <select
                                  style={{ ...sheetInput, fontSize: 12, marginBottom: 6, fontWeight: 700 }}
                                  value={row.categoryId}
                                  onChange={(event) => updateItem(index, 'categoryId', event.target.value)}
                                >
                                  <option value="">Category</option>
                                  {categories.filter((category) => category.IsActive !== false).map((category) => (
                                    <option key={category.CategoryId} value={String(category.CategoryId)}>
                                      {category.CategoryName}
                                    </option>
                                  ))}
                                </select>
                                <textarea
                                  style={{ ...sheetInput, minHeight: 52, resize: 'vertical', fontWeight: 600 }}
                                  value={row.description}
                                  onChange={(event) => updateItem(index, 'description', event.target.value)}
                                  placeholder="Line item description"
                                />
                              </>
                            ) : null}
                          </div>

                          <div style={{ padding: '10px 8px', background: rightBackground, borderTop: '1px solid #fff', borderRight: '1px solid #fff', display: 'flex', alignItems: 'center' }}>
                            {row ? (
                              <input
                                style={{ ...sheetInput, textAlign: 'center', fontWeight: 800 }}
                                type="number"
                                step="0.01"
                                value={row.unitPrice}
                                onChange={(event) => updateItem(index, 'unitPrice', event.target.value)}
                              />
                            ) : null}
                          </div>

                          <div style={{ padding: '10px 8px', background: rightBackground, borderTop: '1px solid #fff', borderRight: '1px solid #fff', display: 'flex', alignItems: 'center' }}>
                            {row ? (
                              <input
                                style={{ ...sheetInput, textAlign: 'center', fontWeight: 800 }}
                                type="number"
                                step="0.01"
                                value={row.quantity}
                                onChange={(event) => updateItem(index, 'quantity', event.target.value)}
                              />
                            ) : null}
                          </div>

                          <div style={{ padding: '10px 8px', background: rightBackground, borderTop: '1px solid #fff', borderRight: '1px solid #fff', display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'center', gap: 8 }}>
                            {row ? (
                              <>
                                <div style={{ textAlign: 'right', fontWeight: 800 }}>{formatMoney(amount)}</div>
                                <button
                                  type="button"
                                  onClick={() => removeItemRow(index)}
                                  style={{ border: 'none', borderRadius: 999, background: 'rgba(194,56,56,0.12)', color: '#a02f2f', fontSize: 11, fontWeight: 800, padding: '4px 8px', cursor: 'pointer' }}
                                >
                                  Remove
                                </button>
                              </>
                            ) : null}
                          </div>

                          <div style={{ padding: '10px 8px', background: rightBackground, borderTop: '1px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>
                            {row ? 'Excl. GST' : ''}
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>

                  <div className="wdgf-local-actions" style={{ marginTop: 12 }}>
                    <button
                      type="button"
                      onClick={addItemRow}
                      className="wdgf-button"
                      style={{ borderStyle: 'dashed', color: '#234ea8', borderColor: 'rgba(47, 111, 212, 0.55)', background: 'rgba(234,241,255,0.96)' }}
                    >
                      + Add line item
                    </button>

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={previewRenderedDraftPdf}
                        disabled={busy === 'preview-draft' || busy === 'create' || busy === 'amend'}
                        className="wdgf-button"
                      >
                        {busy === 'preview-draft' ? 'Rendering preview...' : 'Render + preview PDF'}
                      </button>
                      <button
                        type="submit"
                        disabled={busy === 'create' || busy === 'amend'}
                        className="wdgf-button--primary"
                      >
                        {busy === 'create' || busy === 'amend'
                          ? (mode === 'create' ? 'Saving...' : 'Updating...')
                          : (mode === 'create' ? 'Save new invoice' : 'Save amendments')}
                      </button>
                      <button
                        type="button"
                        onClick={() => { void openQuickFinalisePanel(); }}
                        disabled={busy === 'create' || busy === 'amend'}
                        className="wdgf-button"
                        style={{
                          color: '#234ea8',
                          borderColor: 'rgba(47,111,212,0.42)',
                          background: quickSendOpen ? 'rgba(234,241,255,0.96)' : '#fff',
                        }}
                      >
                        {quickSendOpen ? 'Quick finalise open' : 'Finalise + send'}
                      </button>
                    </div>
                    <ActionFeedbackLine
                      tone={feedbackTone}
                      message={feedbackMessage || (postSavePrompt ? `Saved ${postSavePrompt.invoiceNumber}. Finalise now or keep editing.` : null)}
                    >
                      {postSavePrompt ? (
                        <>
                          <button type="button" onClick={() => finaliseSavedInvoice()} className="wdgf-inline-link-button">Finalise</button>
                          <button type="button" onClick={() => setPostSavePrompt(null)} className="wdgf-inline-link-button">Dismiss</button>
                        </>
                      ) : null}
                    </ActionFeedbackLine>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) minmax(220px, 0.72fr)', borderTop: '1px solid rgba(144, 163, 195, 0.22)' }}>
                  <div style={{ padding: '18px 18px 22px', borderRight: '1px solid rgba(144, 163, 195, 0.22)' }}>
                    <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 10 }}>Payment Methods</div>
                    {sender.payId ? <div style={{ fontSize: 14, marginBottom: 6 }}><strong>PayID:</strong> {sender.payId}</div> : null}
                    <div style={{ fontSize: 14, marginBottom: 6 }}><strong>Bank Transfer:</strong> BSB: {sender.bsb} &nbsp;&nbsp; Account No: {sender.accountNumber}</div>
                    <div style={{ fontSize: 14, marginBottom: 6 }}><strong>Account Name:</strong> {sender.accountName}</div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#a02f2f', marginTop: 10 }}>Please use reference: {paymentReference}</div>
                    {visibleStripePaymentUrl ? (
                      <div style={{ fontSize: 13, marginTop: 10, lineHeight: 1.5 }}>
                        <a href={visibleStripePaymentUrl} target="_blank" rel="noreferrer" style={stripePaymentPreviewLinkStyle}>
                          {formatStripePaymentLabelForPdf(visibleStripePaymentUrl, editorForm.stripeSurchargeEnabled)}
                        </a>
                      </div>
                    ) : null}
                  </div>

                  <div style={{ padding: '18px 18px 22px' }}>
                    <div style={{ display: 'grid', gap: 8, fontSize: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                        <span>Sub Total:</span>
                        <strong>{formatMoney(totals.subTotal)}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                        <span>10% GST:</span>
                        <strong>{formatMoney(totals.gst)}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                        <span>Paid:</span>
                        <strong>-$0.00</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, background: 'linear-gradient(135deg, #35527f 0%, #314865 100%)', color: '#fff', borderRadius: 10, padding: '10px 12px', fontSize: 20 }}>
                        <span>Grand Total:</span>
                        <strong>{formatMoney(totals.grandTotal)}</strong>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ borderTop: '1px solid #d1d5db', padding: '16px 18px', fontSize: 13, color: '#334155', lineHeight: 1.7 }}>
                  <div style={{ fontWeight: 800 }}>{sender.displayName}</div>
                  <div>Address: {sender.address}</div>
                  <div>M: {sender.mobile}</div>
                  <div>E: {sender.email}</div>
                </div>

                {quickSendOpen ? (
                  <div
                    ref={quickSendPanelRef}
                    style={{
                      borderTop: '1px solid rgba(144, 163, 195, 0.22)',
                      padding: '18px',
                      display: 'grid',
                      gap: 14,
                      background: 'linear-gradient(180deg, rgba(248,250,252,0.96) 0%, rgba(255,255,255,0.98) 100%)',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: '#10243f' }}>Quick finalise and send</div>
                        <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, marginTop: 4 }}>
                          Stay on the invoice sheet while reviewing recipient email, previewing the message, and filing or sending the current invoice.
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setQuickSendOpen(false)}
                        className="wdgf-button"
                        style={{ color: '#475569' }}
                      >
                        Hide
                      </button>
                    </div>

                    <div className="wdgf-notice wdgf-notice--warning" style={{ fontSize: 13, lineHeight: 1.6 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#34579e' }}>Invoice ready for send</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: '#10243f', marginTop: 4 }}>{editorForm.invoiceNumber || 'Save invoice first'}</div>
                        </div>
                        {currentInvoiceStatus ? (
                          <div style={{ alignSelf: 'flex-start', fontSize: 12, fontWeight: 700, background: '#fff', border: '1px solid rgba(144,163,195,0.24)', borderRadius: 999, padding: '4px 8px', color: '#34579e' }}>
                            {currentInvoiceStatus}
                          </div>
                        ) : null}
                      </div>
                      <div><strong>Client:</strong> {editorForm.clientName || 'No client details loaded yet'}</div>
                      <div><strong>SharePoint target:</strong> {latestSharePointPath || (isInspectionInvoice ? `${inspectionFolderPath || 'Inspection jobs'} / 1-Invoice` : 'Quotation project folder / Invoice Receivable')}</div>
                      {visibleStripePaymentUrl ? (
                        <div>
                          <a href={visibleStripePaymentUrl} target="_blank" rel="noreferrer" style={stripePaymentPreviewLinkStyle}>
                            {formatStripePaymentLabelForPdf(visibleStripePaymentUrl, editorForm.stripeSurchargeEnabled)}
                          </a>
                        </div>
                      ) : null}
                    </div>

                    <label style={{ display: 'grid', gap: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', color: '#34579e', textTransform: 'uppercase' }}>Recipient email</span>
                      <ClientEmailAutocompleteInput
                        style={inputBase}
                        type="email"
                        value={sendForm.recipientEmail}
                        onChange={updateSendRecipientEmail}
                        placeholder="Recipient email"
                      />
                      <span style={{ fontSize: 12, color: '#64748b' }}>
                        Required when sending from PMapp. Optional if you already sent the invoice manually and just want to mark it as sent here.
                      </span>
                    </label>
                    <label style={{ display: 'grid', gap: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', color: '#34579e', textTransform: 'uppercase' }}>Additional recipients</span>
                      <ClientEmailAutocompleteInput
                        mode="list"
                        style={inputBase}
                        value={sendForm.additionalRecipientEmailsText}
                        onChange={updateSendAdditionalRecipients}
                        placeholder="Comma-separated additional To emails"
                      />
                    </label>
                    <label style={{ display: 'grid', gap: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', color: '#34579e', textTransform: 'uppercase' }}>Additional CC</span>
                      <ClientEmailAutocompleteInput
                        mode="list"
                        style={inputBase}
                        value={sendForm.additionalCcEmailsText}
                        onChange={updateSendAdditionalCc}
                        placeholder="Comma-separated CC emails"
                      />
                    </label>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      <strong>To list:</strong> {sendRecipientEmails.length > 0 ? sendRecipientEmails.join(', ') : 'No recipient email set yet.'}
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      <strong>Final CC list:</strong> {sendForm.ccEmails.length > 0 ? sendForm.ccEmails.join(', ') : 'No CC emails set for this invoice.'}
                    </div>

                    {renderEmailPreviewComposer(360)}

                    <div style={{ borderRadius: 12, background: '#f8fafc', border: '1px solid #e2e8f0', padding: 12, color: '#334155', fontSize: 13, lineHeight: 1.6 }}>
                      <div style={{ fontWeight: 800, marginBottom: 4 }}>Stored draft PDF</div>
                      <div>Source: {sendForm.pdfFile ? 'Manual PDF override selected for confirmation' : (storedDraftPdf?.draftFileId ? 'Auto-rendered invoice draft in blob storage' : 'No linked draft yet')}</div>
                      <div>File: {sendForm.pdfFile?.name || storedDraftPdf?.fileName || 'Not available'}</div>
                      <div>Stored: {sendForm.pdfFile ? 'Will use the selected local PDF' : formatDraftTimestamp(storedDraftPdf?.createdDate)}</div>
                      {storedDraftLink ? (
                        <div>
                          Blob link:{' '}
                          <a href={storedDraftLink} target="_blank" rel="noreferrer" style={{ color: '#0f766e', fontWeight: 700 }}>
                            Open stored draft blob
                          </a>
                        </div>
                      ) : null}
                    </div>

                    <div className="wdgf-action-row">
                      <button
                        type="button"
                        onClick={previewRenderedDraftPdf}
                        disabled={busy === 'preview-draft'}
                        className="wdgf-button"
                      >
                        {busy === 'preview-draft' ? 'Rendering preview...' : 'Render + preview draft'}
                      </button>
                      <button
                        type="button"
                        onClick={() => submitSendLock('file')}
                        disabled={busy === 'send-lock'}
                        className="wdgf-button--primary"
                      >
                        {busy === 'send-lock' ? 'Filing...' : 'Confirm and file'}
                      </button>
                      <button
                        type="button"
                        onClick={() => submitSendLock('manual')}
                        disabled={busy === 'send-lock'}
                        className="wdgf-button"
                        style={{ color: '#234ea8', borderColor: 'rgba(47,111,212,0.42)' }}
                      >
                        {busy === 'send-lock' ? 'Saving...' : 'Confirm, file, and mark emailed manually'}
                      </button>
                      <button
                        type="button"
                        onClick={() => submitSendLock('email')}
                        disabled={busy === 'send-lock'}
                        className="wdgf-button--primary"
                      >
                        {busy === 'send-lock' ? 'Sending...' : 'Confirm, file, and email'}
                      </button>
                      {inspectionCalendarCanOpen ? (
                        <button
                          type="button"
                          onClick={() => openInspectionCalendarInvite()}
                          disabled={busy === 'inspection-calendar'}
                          className="wdgf-button--primary"
                        >
                          {busy === 'inspection-calendar' ? 'Creating invite...' : 'Create inspection calendar invite'}
                        </button>
                      ) : null}
                    </div>
                    <ActionFeedbackLine tone={feedbackTone} message={feedbackMessage} />
                  </div>
                ) : null}
              </div>
              ) : null}
            </div>
          </form>
        </div>

        <div style={{ display: activeTab === 'finalise' || activeTab === 'categories' ? 'grid' : 'none', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 18 }}>
          <div style={{ display: activeTab === 'finalise' ? 'block' : 'none', background: '#fff', border: '1px solid rgba(148,163,184,0.18)', borderRadius: 18, padding: 18 }}>
            <h2 style={{ margin: '0 0 12px', fontSize: 18 }}>Finalise and send</h2>
            <div style={{ marginBottom: 12, color: '#475569', fontSize: 13, lineHeight: 1.6 }}>
              Previewing or confirming from this tab refreshes the invoice date and due date before the draft PDF is stored, filed, or emailed.
            </div>
            <form onSubmit={(event) => event.preventDefault()} style={{ display: 'grid', gap: 12 }}>
              <div className="wdgf-subpanel">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 800, color: '#0f172a' }}>Pending to send</div>
                    <div style={{ fontSize: 13, color: '#475569' }}>
                      {pendingInvoiceOptions.length} invoice{pendingInvoiceOptions.length === 1 ? '' : 's'} currently still pending send or filing.
                    </div>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#34579e' }}>
                    {selectedPendingInvoice?.InvoiceNumber ? `Loaded for filing: ${selectedPendingInvoice.InvoiceNumber}` : 'Choose a draft to load below'}
                  </div>
                </div>
                <input
                  style={inputBase}
                  value={finaliseLookup}
                  onChange={(event) => setFinaliseLookup(event.target.value)}
                  placeholder="Search pending invoices by number, client, or address"
                />
                <div style={{ fontSize: 12, color: '#64748b' }}>
                  Choose a pending draft below. The selected row becomes the invoice used by the finalise buttons.
                </div>
                {filteredPendingInvoiceOptions.length > 0 ? (
                  <div style={{ display: 'grid', gap: 8, maxHeight: filteredPendingInvoiceOptions.length > 3 ? 240 : undefined, overflowY: filteredPendingInvoiceOptions.length > 3 ? 'auto' : undefined, paddingRight: filteredPendingInvoiceOptions.length > 3 ? 4 : 0 }}>
                    {filteredPendingInvoiceOptions.map((invoice) => (
                      (() => {
                        const isSelected = String(invoice.InvoiceId || '').trim() === String(sendForm.invoiceId || '').trim()
                          || normalizeLookup(invoice.InvoiceNumber) === normalizeLookup(editorForm.invoiceNumber || finaliseLookup);
                        return (
                      <button
                        key={String(invoice.InvoiceId || invoice.InvoiceNumber)}
                        type="button"
                        onClick={() => void openPendingInvoiceForFinalise(invoice)}
                        style={{
                          border: isSelected ? '1px solid rgba(47,111,212,0.42)' : '1px solid #e2e8f0',
                          borderRadius: 12,
                          background: isSelected ? 'rgba(234,241,255,0.96)' : '#fff',
                          padding: '12px 14px',
                          textAlign: 'left',
                          cursor: 'pointer',
                          boxShadow: isSelected ? '0 10px 24px rgba(35,78,168,0.14)' : 'none',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                          <div>
                            <strong style={{ color: '#0f172a' }}>{invoice.InvoiceNumber || `Invoice ${invoice.InvoiceId}`}</strong>
                            <div style={{ fontSize: 12, color: isSelected ? '#34579e' : '#64748b', marginTop: 4 }}>
                              {isSelected ? 'Loaded into the finalise form' : 'Select this draft for preview, filing, or email'}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            <span style={{ fontSize: 12, color: '#34579e', fontWeight: 700, background: '#fff', border: '1px solid rgba(144,163,195,0.24)', borderRadius: 999, padding: '4px 8px' }}>
                              {invoice.Status || 'Draft'}
                            </span>
                            {isSelected ? (
                              <span style={{ fontSize: 12, color: '#fff', fontWeight: 700, background: '#234ea8', borderRadius: 999, padding: '4px 8px' }}>
                                Selected
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <div style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>
                          {invoice.Client || invoice.Description || 'No client details'}
                        </div>
                      </button>
                        );
                      })()
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 13, color: '#64748b' }}>
                    {pendingInvoiceOptions.length === 0 ? 'No pending invoices found in the current invoice list.' : 'No pending invoices match this search yet.'}
                  </div>
                )}
              </div>
              <div className="wdgf-notice wdgf-notice--warning" style={{ fontSize: 13, lineHeight: 1.6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#34579e' }}>Draft Selected For Filing</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#10243f', marginTop: 4 }}>{selectedFinaliseInvoiceLabel}</div>
                  </div>
                  {selectedPendingInvoice?.Status ? (
                    <div style={{ alignSelf: 'flex-start', fontSize: 12, fontWeight: 700, background: '#fff', border: '1px solid rgba(144,163,195,0.24)', borderRadius: 999, padding: '4px 8px', color: '#34579e' }}>
                      {selectedPendingInvoice.Status}
                    </div>
                  ) : null}
                </div>
                <div style={{ marginBottom: 6 }}>This is the draft the actions below will preview, file, or email.</div>
                <div><strong>Client:</strong> {selectedPendingInvoice?.Client || editorForm.clientName || 'No client details loaded yet'}</div>
                {editorForm.rfiTicketNumber ? <div><strong>RFI:</strong> {editorForm.rfiTicketNumber}</div> : null}
                {visibleStripePaymentUrl ? (
                  <div>
                    <a href={visibleStripePaymentUrl} target="_blank" rel="noreferrer" style={stripePaymentPreviewLinkStyle}>
                      {formatStripePaymentLabelForPdf(visibleStripePaymentUrl, editorForm.stripeSurchargeEnabled)}
                    </a>
                  </div>
                ) : null}
                <div><strong>SharePoint target:</strong> {latestSharePointPath || (isInspectionInvoice ? `${inspectionFolderPath || 'Inspection jobs'} / 1-Invoice` : 'Quotation project folder / Invoice Receivable')}</div>
                {isInspectionInvoice ? (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
                    <StatusBadge status={inspectionFolderReady ? 'Filed' : 'Draft'} size="sm">
                      {inspectionFolderReady ? 'Folder ready' : 'Folder pending'}
                    </StatusBadge>
                    <button
                      type="button"
                      onClick={createInspectionFolder}
                      disabled={busy === 'inspection-folder' || !editorForm.invoiceId}
                      className="wdgf-button"
                      style={{ padding: '6px 10px' }}
                    >
                      {busy === 'inspection-folder' ? 'Creating...' : 'Create folder'}
                    </button>
                    {inspectionFolderUrl ? (
                      <a href={inspectionFolderUrl} target="_blank" rel="noreferrer" style={{ color: '#234ea8', fontWeight: 700 }}>
                        Open inspection folder
                      </a>
                    ) : null}
                  </div>
                ) : null}
                {storedDraftLink ? (
                  <div>
                    <strong>Stored draft blob:</strong>{' '}
                    <a href={storedDraftLink} target="_blank" rel="noreferrer" style={{ color: '#234ea8', fontWeight: 700 }}>
                      Open stored draft blob
                    </a>
                  </div>
                ) : null}
                {latestSharePointLink ? (
                  <div>
                    <strong>Saved file link:</strong>{' '}
                    <a href={latestSharePointLink} target="_blank" rel="noreferrer" style={{ color: '#234ea8', fontWeight: 700 }}>
                      Open saved SharePoint file
                    </a>
                  </div>
                ) : null}
              </div>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', color: '#34579e', textTransform: 'uppercase' }}>Recipient email</span>
                <ClientEmailAutocompleteInput
                  style={inputBase}
                  type="email"
                  value={sendForm.recipientEmail}
                  onChange={updateSendRecipientEmail}
                  placeholder="Recipient email"
                />
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  Required when sending from PMapp. Optional if you already sent the invoice manually and just want to mark it as sent here.
                </span>
              </label>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', color: '#34579e', textTransform: 'uppercase' }}>Additional recipients</span>
                <ClientEmailAutocompleteInput
                  mode="list"
                  style={inputBase}
                  value={sendForm.additionalRecipientEmailsText}
                  onChange={updateSendAdditionalRecipients}
                  placeholder="Comma-separated additional To emails"
                />
              </label>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', color: '#34579e', textTransform: 'uppercase' }}>Additional CC</span>
                <ClientEmailAutocompleteInput
                  mode="list"
                  style={inputBase}
                  value={sendForm.additionalCcEmailsText}
                  onChange={updateSendAdditionalCc}
                  placeholder="Comma-separated CC emails"
                />
              </label>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                <strong>To list:</strong> {sendRecipientEmails.length > 0 ? sendRecipientEmails.join(', ') : 'No recipient email set yet.'}
              </div>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                <strong>Final CC list:</strong> {sendForm.ccEmails.length > 0 ? sendForm.ccEmails.join(', ') : 'No CC emails set for this invoice.'}
              </div>
              {renderEmailPreviewComposer(420)}
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', color: '#34579e', textTransform: 'uppercase' }}>PDF override</span>
                <input
                  style={inputBase}
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(event) => setSendForm((prev) => ({ ...prev, pdfFile: event.target.files?.[0] || null }))}
                />
              </label>
              <div style={{ borderRadius: 12, background: '#f8fafc', border: '1px solid #e2e8f0', padding: 12, color: '#334155', fontSize: 13, lineHeight: 1.6 }}>
                <div style={{ fontWeight: 800, marginBottom: 4 }}>Stored draft PDF</div>
                <div>Source: {sendForm.pdfFile ? 'Manual PDF override selected for confirmation' : (storedDraftPdf?.draftFileId ? 'Auto-rendered invoice draft in blob storage' : 'No linked draft yet')}</div>
                <div>File: {sendForm.pdfFile?.name || storedDraftPdf?.fileName || 'Not available'}</div>
                <div>Stored: {sendForm.pdfFile ? 'Will use the selected local PDF' : formatDraftTimestamp(storedDraftPdf?.createdDate)}</div>
                <div>
                  Blob file ID:{' '}
                  <span title={storedDraftPdf?.draftFileId || undefined}>
                    {formatCompactId(storedDraftPdf?.draftFileId)}
                  </span>
                </div>
                {storedDraftLink ? (
                  <div>
                    Blob link:{' '}
                    <a href={storedDraftLink} target="_blank" rel="noreferrer" style={{ color: '#0f766e', fontWeight: 700 }}>
                      Open stored draft blob
                    </a>
                  </div>
                ) : null}
              </div>
              {latestSendLockData?.status ? (
                <div style={{ borderRadius: 12, background: '#ecfdf5', border: '1px solid #bbf7d0', padding: 12, color: '#166534', fontSize: 13, lineHeight: 1.7 }}>
                  <div style={{ fontWeight: 800, marginBottom: 4 }}>Latest SharePoint filing</div>
                  <div><strong>Status:</strong> {latestSendLockData.status}</div>
                  <div><strong>Invoice:</strong> {latestSendLockData.invoiceNumber || editorForm.invoiceNumber || 'Current invoice'}</div>
                  <div><strong>Saved to:</strong> {latestSharePointPath || 'SharePoint path not returned'}</div>
                  <div><strong>PDF source:</strong> {latestSendLockData.pdfSource || 'Rendered draft'}</div>
                  {latestVisibleStripePaymentUrl ? (
                    <div>
                      <strong>Stripe:</strong>{' '}
                      <a
                        href={latestVisibleStripePaymentUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: '#166534', fontWeight: 700 }}
                      >
                        Open payment link
                      </a>
                    </div>
                  ) : null}
                  {latestSendLockData.email?.attempted ? (
                    <>
                      <div><strong>Email:</strong> {latestSendLockData.email.sent ? `Sent to ${(latestSendLockData.email.recipients?.length ? latestSendLockData.email.recipients.join(', ') : (latestSendLockData.email.recipient || sendForm.recipientEmail || 'recipient'))}` : `Attempted but not sent${latestSendLockData.email.error ? `: ${latestSendLockData.email.error}` : ''}`}</div>
                      <div><strong>CC:</strong> {latestSendLockData.email.ccRecipients?.length ? latestSendLockData.email.ccRecipients.join(', ') : 'None'}</div>
                    </>
                  ) : latestSendLockData.email?.manual ? (
                    <>
                      <div><strong>Email:</strong> {`Marked as sent manually${latestSendLockData.email.recipients?.length ? ` to ${latestSendLockData.email.recipients.join(', ')}` : (latestSendLockData.email.recipient || sendForm.recipientEmail ? ` to ${latestSendLockData.email.recipient || sendForm.recipientEmail}` : '')}`}</div>
                      <div><strong>CC:</strong> {latestSendLockData.email.ccRecipients?.length ? latestSendLockData.email.ccRecipients.join(', ') : 'None'}</div>
                    </>
                  ) : null}
                  {storedDraftLink ? (
                    <div>
                      <strong>Draft blob:</strong>{' '}
                      <a href={storedDraftLink} target="_blank" rel="noreferrer" style={{ color: '#166534', fontWeight: 700 }}>
                        Open stored draft blob
                      </a>
                    </div>
                  ) : null}
                  {latestSharePointLink ? (
                    <div>
                      <strong>Open file:</strong>{' '}
                      <a href={latestSharePointLink} target="_blank" rel="noreferrer" style={{ color: '#166534', fontWeight: 700 }}>
                        Open saved invoice in SharePoint
                      </a>
                    </div>
                  ) : null}
                </div>
              ) : null}
              <div className="wdgf-action-row">
                <button
                  type="button"
                  onClick={previewRenderedDraftPdf}
                  disabled={busy === 'preview-draft'}
                  className="wdgf-button"
                >
                  {busy === 'preview-draft' ? 'Rendering preview...' : 'Render + preview draft'}
                </button>
                <button
                  type="button"
                  onClick={reviewStoredDraftPdf}
                  disabled={busy === 'review-draft' || !storedDraftPdf?.draftFileId}
                  className="wdgf-button"
                >
                  {busy === 'review-draft' ? 'Opening...' : 'Review stored draft'}
                </button>
                <button
                  type="button"
                  onClick={() => submitSendLock('file')}
                  disabled={busy === 'send-lock'}
                  className="wdgf-button--primary"
                >
                  {busy === 'send-lock' ? 'Filing...' : 'Confirm and file'}
                </button>
                <button
                  type="button"
                  onClick={() => submitSendLock('manual')}
                  disabled={busy === 'send-lock'}
                  className="wdgf-button"
                  style={{ color: '#234ea8', borderColor: 'rgba(47,111,212,0.42)' }}
                >
                  {busy === 'send-lock' ? 'Saving...' : 'Confirm, file, and mark emailed manually'}
                </button>
                <button
                  type="button"
                  onClick={() => submitSendLock('email')}
                  disabled={busy === 'send-lock'}
                  className="wdgf-button--primary"
                >
                  {busy === 'send-lock' ? 'Sending...' : 'Confirm, file, and email'}
                </button>
                {inspectionCalendarCanOpen ? (
                  <button
                    type="button"
                    onClick={() => openInspectionCalendarInvite()}
                    disabled={busy === 'inspection-calendar'}
                    className="wdgf-button--primary"
                  >
                    {busy === 'inspection-calendar' ? 'Creating invite...' : 'Create inspection calendar invite'}
                  </button>
                ) : null}
              </div>
              <ActionFeedbackLine tone={feedbackTone} message={feedbackMessage} />
            </form>
          </div>

          <div className="wdgf-subpanel" style={{ display: activeTab === 'categories' ? 'block' : 'none' }}>
            <h2 style={{ margin: '0 0 12px', fontSize: 18 }}>Reusable line categories</h2>
            <form onSubmit={submitCategory} style={{ display: 'grid', gap: 10 }}>
              {categoryForm.categoryId ? (
                <div style={{ fontSize: 12, color: '#34579e', fontWeight: 700 }}>
                  Editing category #{categoryForm.categoryId}
                </div>
              ) : null}
              <input style={inputBase} placeholder="Code (optional)" value={categoryForm.categoryCode} onChange={(event) => setCategoryForm((prev) => ({ ...prev, categoryCode: event.target.value }))} />
              <input style={inputBase} placeholder="Category name" value={categoryForm.categoryName} onChange={(event) => setCategoryForm((prev) => ({ ...prev, categoryName: event.target.value }))} required />
              <input style={inputBase} placeholder="Default description" value={categoryForm.defaultDescription} onChange={(event) => setCategoryForm((prev) => ({ ...prev, defaultDescription: event.target.value }))} />
              <div className="wdgf-action-row">
                <button
                  type="submit"
                  disabled={busy === 'category'}
                  className="wdgf-button--primary"
                >
                  {busy === 'category' ? 'Saving...' : (categoryForm.categoryId ? 'Update category' : 'Save category')}
                </button>
                {categoryForm.categoryId ? (
                  <button
                    type="button"
                    onClick={resetCategoryForm}
                    className="wdgf-button"
                    style={{ color: '#234ea8', borderColor: 'rgba(47,111,212,0.42)' }}
                  >
                    Clear editor
                  </button>
                ) : null}
              </div>
              <ActionFeedbackLine tone={feedbackTone} message={feedbackMessage} />
            </form>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
              {categories.map((category) => (
                <button
                  key={category.CategoryId}
                  type="button"
                  onClick={() => loadCategoryIntoForm(category)}
                  style={{
                    borderRadius: 999,
                    background: String(categoryForm.categoryId) === String(category.CategoryId) ? '#3456b8' : 'rgba(234,241,255,0.96)',
                    color: String(categoryForm.categoryId) === String(category.CategoryId) ? '#fff' : '#34579e',
                    padding: '6px 10px',
                    fontSize: 12,
                    fontWeight: 700,
                    border: '1px solid rgba(144,163,195,0.24)',
                    cursor: 'pointer',
                  }}
                  title={`Load ${category.CategoryName} into the editor`}
                >
                  {category.CategoryName}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      {inspectionCalendarOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="inspection-calendar-title"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 80,
            display: 'grid',
            placeItems: 'center',
            padding: 18,
            background: 'rgba(15,23,42,0.42)',
          }}
        >
          <div
            style={{
              width: 'min(640px, 100%)',
              borderRadius: 18,
              background: '#fff',
              boxShadow: '0 24px 70px rgba(15,23,42,0.28)',
              border: '1px solid rgba(148,163,184,0.22)',
              maxHeight: 'calc(100vh - 36px)',
              overflowX: 'hidden',
              overflowY: 'auto',
            }}
          >
            <div style={{ padding: '18px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
              <div>
                <div id="inspection-calendar-title" style={{ fontSize: 18, fontWeight: 900, color: '#10243f' }}>
                  Create inspection calendar invite
                </div>
                <div style={{ marginTop: 4, color: '#475569', fontSize: 13, lineHeight: 1.5 }}>
                  Creates a normal Outlook calendar event in the invoice sender account. Teams meeting stays off.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setInspectionCalendarOpen(false)}
                className="wdgf-button"
                style={{ color: '#475569', padding: '8px 12px' }}
              >
                Close
              </button>
            </div>
            <div style={{ padding: 20, display: 'grid', gap: 14 }}>
              <div style={{ borderRadius: 12, background: '#f8fafc', border: '1px solid #e2e8f0', padding: 12, color: '#334155', fontSize: 13, lineHeight: 1.6 }}>
                <div><strong>Subject:</strong> {inspectionCalendarSubject}</div>
                <div><strong>Calendar:</strong> {inspectionCalendarOwnerPreview}</div>
                <div><strong>Location:</strong> {editorForm.projectAddress || 'Inspection address not set'}</div>
                <div><strong>Invitee:</strong> {inspectionCalendarForm.attendeeEmail || 'Invoice client email not set'}</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', color: '#34579e', textTransform: 'uppercase' }}>Date</span>
                  <input
                    style={inputBase}
                    type="date"
                    value={inspectionCalendarForm.date}
                    onChange={(event) => setInspectionCalendarForm((prev) => ({ ...prev, date: event.target.value }))}
                  />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', color: '#34579e', textTransform: 'uppercase' }}>Time</span>
                  <input
                    style={inputBase}
                    type="time"
                    value={inspectionCalendarForm.time}
                    onChange={(event) => setInspectionCalendarForm((prev) => ({ ...prev, time: event.target.value }))}
                  />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', color: '#34579e', textTransform: 'uppercase' }}>Duration</span>
                  <input
                    style={inputBase}
                    type="number"
                    min="1"
                    max="720"
                    step="15"
                    value={inspectionCalendarForm.durationMinutes}
                    onChange={(event) => setInspectionCalendarForm((prev) => ({ ...prev, durationMinutes: event.target.value }))}
                  />
                </label>
              </div>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', color: '#34579e', textTransform: 'uppercase' }}>Invitee</span>
                <input
                  style={inputBase}
                  type="email"
                  value={inspectionCalendarForm.attendeeEmail}
                  onChange={(event) => setInspectionCalendarForm((prev) => ({ ...prev, attendeeEmail: event.target.value }))}
                  placeholder="Invoice client email"
                />
              </label>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', color: '#34579e', textTransform: 'uppercase' }}>Description</span>
                <textarea
                  style={{ ...inputBase, minHeight: 88, resize: 'vertical', lineHeight: 1.5 }}
                  value={inspectionCalendarForm.description}
                  onChange={(event) => setInspectionCalendarForm((prev) => ({ ...prev, description: event.target.value }))}
                  placeholder="Add details or instructions for the inspection"
                />
              </label>
              {inspectionCalendarError ? (
                <div className="wdgf-action-feedback wdgf-action-feedback--danger" role="alert">
                  {inspectionCalendarError}
                </div>
              ) : null}
              {inspectionCalendarResult ? (
                <div className="wdgf-action-feedback wdgf-action-feedback--success" role="status">
                  <span>
                    Created calendar invite for {inspectionCalendarResult.attendeeEmail || inspectionCalendarForm.attendeeEmail}.
                  </span>
                  {inspectionCalendarResult.event?.webLink ? (
                    <span className="wdgf-action-feedback__actions">
                      <a href={inspectionCalendarResult.event.webLink} target="_blank" rel="noreferrer">
                        Open in Outlook
                      </a>
                    </span>
                  ) : null}
                </div>
              ) : null}
              <div className="wdgf-action-row" style={{ justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setInspectionCalendarOpen(false)}
                  className="wdgf-button"
                  style={{ color: '#475569' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitInspectionCalendarInvite}
                  disabled={busy === 'inspection-calendar'}
                  className="wdgf-button--primary"
                >
                  {busy === 'inspection-calendar' ? 'Creating...' : 'Create invite'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default InvoiceWorkflowPage;
